import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PatientFinancial from './components/PatientFinancial';
import SignaturePad from './components/SignaturePad';
import axios from 'axios';
import EMRNotes from './EMRNotes';
import EMRRecords from './EMRRecords';
import TPSpine from './TPSpine';
import ClientSigningPass from './components/ClientSigningPass';
import StaffCompletionPass from './components/StaffCompletionPass';
import { PhotoGrid, PatientAvatar, PhotoUpload } from './EMRPhotos';
import EMRGroups from './EMRGroups';
import Rounds from './Rounds';
import VitalsPanel, { VitalsModal } from './VitalsPanel';
import MedPassSettings from './MedPassSettings';

import { CareToday, ContactsReleases, SafetySnapshot, TreatmentTeam, QuickActions, UrAuthCard, RecentlyResulted, InsuranceCard, AdministrativeCard } from './OverviewCards';
import ChartAudit from './ChartAudit';
import EMRLabs from './EMRLabs';
import LabsBoard from './LabsBoard';
import EMRSchedule from './EMRSchedule';
import EMRCompliance from './EMRCompliance';
import CosignQueue from './CosignQueue';
import ProviderQueue from './ProviderQueue';
import MyChartQuestions, { useChartQuestionCount } from './components/MyChartQuestions';
import MyCompliance, { useComplianceCount } from './components/MyCompliance';
import FormBuilder from './components/FormBuilder';

import { T } from './theme';
import SegmentedTabs from './components/SegmentedTabs';
import StatusDot from './components/StatusDot';
import { TcioInsights } from './components/AiPrimitives';

// ---- Prior EMR record (mig 344): the chart downloaded from the previous EMR, read by TCIO, applied section by section by a person.
const PR_SECTIONS = [
  ['identifiers', 'Stay identifiers', 'Prior MRN and admit date onto this stay (admit date only if blank).'],
  ['demographics', 'Demographics', 'DOB, sex, address, phone, email, marital status — blank fields only, nothing overwritten.'],
  ['contacts', 'Contacts', 'Emergency and family contacts not already on the chart.'],
  ['allergies', 'Allergies', 'Allergies not already on the chart; NKDA only when the chart has none.'],
  ['diagnoses', 'Diagnoses', 'Clinical roles confirm; written as active diagnoses on this stay.'],
  ['medications', 'Medications', 'Each becomes a Doctor\'s Order in pending review, authorized by the attached signed order pages. A nurse verifies each one against the signed order and activates it; the prescriber does not re-sign.'],
];
const prBox = { background:'#fff', border:'1px solid #e6e9ee', borderRadius:12, padding:14, marginBottom:12 };
const prBtn = (primary, disabled) => ({ padding:'6px 12px', borderRadius:8, border: primary ? 'none' : '1px solid #d5dbe3', background: primary ? '#0878E8' : '#fff', color: primary ? '#fff' : '#0A2239', fontWeight:600, fontSize:12.5, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 });
const prMuted = { color:'#5b6b7c', fontSize:12 };
function PrDrop({ onFiles, multiple, busy, hint }) {
  const [over, setOver] = React.useState(false); const ref = React.useRef(null);
  return (
    <div onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); const fs = [...(e.dataTransfer.files || [])].filter(f => /\.pdf$/i.test(f.name)); if (fs.length) onFiles(fs); }}
      onClick={() => !busy && ref.current && ref.current.click()}
      style={{ border:'2px dashed ' + (over ? '#0878E8' : '#c9d2dd'), background: over ? '#eef5ff' : '#fafbfc', borderRadius:12, padding:'18px 14px', textAlign:'center', cursor: busy ? 'default' : 'pointer' }}>
      <input ref={ref} type="file" accept="application/pdf" multiple={!!multiple} style={{ display:'none' }} onChange={e => { const fs = [...(e.target.files || [])]; e.target.value = ''; if (fs.length) onFiles(fs); }} />
      <div style={{ fontWeight:600, fontSize:13.5, color:'#0A2239' }}>{busy ? 'Uploading…' : (multiple ? 'Drop the chart PDFs here, or click to choose' : 'Drop the chart PDF here, or click to choose')}</div>
      <div style={{ ...prMuted, marginTop:4 }}>{hint || 'Download the client\'s full chart from the previous EMR as a PDF. It is kept on the chart as the prior record and read so this chart can be filled.'}</div>
    </div>
  );
}
function PriorRecordPanel({ patientId }) {
  const [data, setData] = React.useState(null); const [busy, setBusy] = React.useState(false); const [msg, setMsg] = React.useState(null); const [applying, setApplying] = React.useState('');
  const load = React.useCallback(async () => { try { setData((await axios.get(API + '/api/emr/patients/' + patientId + '/prior-record')).data); } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } }, [patientId]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { if (!data || !data.records.some(r => r.status === 'reading')) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [data, load]);
  const onFiles = async (files) => { setBusy(true); setMsg(null);
    try { const fd = new FormData(); fd.append('file', files[0]); await axios.post(API + '/api/emr/patients/' + patientId + '/prior-record', fd); setMsg({ ok: 'Uploaded. Reading the chart now — this takes a minute for a long one.' }); load(); }
    catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } finally { setBusy(false); } };
  const apply = async (attId, section) => { setApplying(attId + section); setMsg(null);
    try { const r = (await axios.post(API + '/api/emr/prior-record/' + attId + '/apply', { section })).data; setMsg({ ok: section + ': ' + r.added + ' added' + (r.skipped ? ', ' + r.skipped + ' already on the chart' : '') + (r.detail && r.detail.length ? ' — ' + r.detail.slice(0, 8).join(', ') : '') }); load(); }
    catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } finally { setApplying(''); } };
  const reread = async (attId) => { setBusy(true); try { await axios.post(API + '/api/emr/prior-record/' + attId + '/reread'); load(); } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } finally { setBusy(false); } };
  if (!data) return null;
  const canFor = (s) => s === 'diagnoses' ? data.may_confirm_dx : s === 'medications' ? data.may_apply_meds : data.may_apply;
  const countFor = (x, s) => s === 'contacts' ? (x.contacts || []).length : s === 'allergies' ? ((x.allergies && x.allergies.items) || []).length || (x.allergies && x.allergies.nkda ? 1 : 0) : s === 'diagnoses' ? (x.diagnoses || []).length : s === 'medications' ? ((x.medications && x.medications.items) || []).length : null;
  const preview = (x, s) => {
    if (s === 'identifiers') return ['MRN ' + (x.identifiers.prior_mrn || '—'), 'admitted ' + (x.identifiers.admit_date || '—'), x.identifiers.loc || x.identifiers.program || ''].filter(Boolean).join(' · ');
    if (s === 'demographics') { const d = x.demographics || {}; const a = d.address || {}; return [d.dob ? 'DOB ' + d.dob : null, d.sex, d.phone, d.email, [a.line1, a.city, a.state, a.zip].filter(Boolean).join(', ')].filter(Boolean).join(' · '); }
    if (s === 'contacts') return (x.contacts || []).map(c => c.name + (c.relationship ? ' (' + c.relationship + ')' : '')).join(', ');
    if (s === 'allergies') return x.allergies && x.allergies.items && x.allergies.items.length ? x.allergies.items.map(a => a.allergen).join(', ') : (x.allergies && x.allergies.nkda ? 'NKDA' : '');
    if (s === 'diagnoses') return (x.diagnoses || []).map(d => d.icd10_code + (d.is_primary ? '*' : '')).join(', ');
    if (s === 'medications') { const m = x.medications || {}; return m.items && m.items.length ? m.items.map(i => i.medication_name + (i.strength ? ' ' + i.strength : '') + (i.frequency ? ' ' + i.frequency : '')).join('; ') : (m.note || (m.order_pages ? 'no current orders read' : 'no order pages found')); }
    return '';
  };
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontWeight:700, fontSize:13.5, color:'#0A2239', marginBottom:8 }}>Prior EMR record</div>
      <PrDrop onFiles={onFiles} busy={busy} />
      {msg && <div style={{ marginTop:8, fontSize:12.5, color: msg.err ? '#b42318' : '#067647' }}>{msg.err || msg.ok}</div>}
      {data.records.map(r => {
        const x = r.extracted; const ap = r.applied || {};
        return (
          <div key={r.id} style={{ ...prBox, marginTop:10 }}>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <strong style={{ fontSize:13 }}>{r.filename}</strong>
              <span style={prMuted}>uploaded {new Date(r.created_at).toLocaleDateString()} by {r.uploaded_by_name || '—'}</span>
              <span style={{ fontSize:11.5, fontWeight:600, padding:'2px 8px', borderRadius:999, background: r.status === 'read' ? '#e6f4ea' : r.status === 'error' ? '#fde8e8' : '#fff4e5', color: r.status === 'read' ? '#067647' : r.status === 'error' ? '#b42318' : '#9a6700' }}>{r.status === 'read' ? 'read' + (x ? ' · ' + x.pages + ' pages' : '') : r.status === 'error' ? 'could not be read' : 'reading…'}</span>
              {r.status === 'error' && <button style={prBtn(false)} onClick={() => reread(r.id)}>Try again</button>}
              {r.status === 'error' && <span style={{ ...prMuted, color:'#b42318' }}>{r.error}</span>}
            </div>
            {x && !x.has_facesheet && <div style={{ ...prMuted, marginTop:6, color:'#9a6700' }}>No facesheet was found in this document, so only what could be read elsewhere is shown.</div>}
            {x && x.insurance && (x.insurance.payer || x.insurance.subscriber_id) && <div style={{ ...prMuted, marginTop:6 }}>Insurance in the prior record (for comparison only — the VOB is the chart's identity): {x.insurance.payer || '—'} · ID {x.insurance.subscriber_id || '—'}</div>}
            {x && PR_SECTIONS.map(([key, title, help]) => {
              const n = countFor(x, key); const done = ap[key]; const empty = n === 0;
              return (
                <div key={key} style={{ display:'grid', gridTemplateColumns: window.innerWidth < 760 ? '1fr' : '160px 1fr auto', gap:10, alignItems:'start', padding:'8px 0', borderTop:'1px solid #eef1f5' }}>
                  <div><div style={{ fontWeight:600, fontSize:12.5 }}>{title}{n != null ? ' (' + n + ')' : ''}</div><div style={prMuted}>{help}</div></div>
                  <div style={{ fontSize:12.5, color:'#1e293b', wordBreak:'break-word' }}>{preview(x, key) || <span style={prMuted}>nothing read for this section</span>}</div>
                  <div>{done ? <span style={{ ...prMuted, color:'#067647' }}>applied by {done.by_name} · {done.added} added</span>
                    : <button style={prBtn(true, empty || !canFor(key) || applying === r.id + key)} disabled={empty || !canFor(key) || applying === r.id + key} onClick={() => apply(r.id, key)} title={!canFor(key) ? 'Your role cannot apply this section' : ''}>{applying === r.id + key ? 'Applying…' : 'Apply to chart'}</button>}</div>
                </div>);
            })}
          </div>);
      })}
    </div>
  );
}
function PriorRecordsImport({ api, facilityId, facilityName }) {
  const [stays, setStays] = React.useState(null); const [busy, setBusy] = React.useState(false); const [results, setResults] = React.useState(null); const [msg, setMsg] = React.useState(null);
  const load = React.useCallback(async () => { try { setStays((await axios.get(api + '/api/emr/facilities/' + facilityId + '/prior-records')).data.stays); } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } }, [api, facilityId]);
  React.useEffect(() => { load(); setResults(null); setMsg(null); }, [load]);
  React.useEffect(() => { if (!stays || !stays.some(s => s.status === 'reading')) return; const t = setInterval(load, 6000); return () => clearInterval(t); }, [stays, load]);
  const onFiles = async (files) => { setBusy(true); setMsg(null); setResults(null);
    try { const fd = new FormData(); files.forEach(f => fd.append('files', f)); const r = (await axios.post(api + '/api/emr/facilities/' + facilityId + '/prior-records/bulk', fd)).data; setResults(r); setMsg({ ok: r.matched + ' matched and uploaded, ' + r.unmatched + ' not stored' }); load(); }
    catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } finally { setBusy(false); } };
  const applied = (s) => s.applied ? Object.keys(s.applied).length : 0;
  return (
    <div>
      <div style={{ fontWeight:700, fontSize:13.5, color:'#0A2239', marginBottom:8 }}>Import prior records — {facilityName}</div>
      <PrDrop multiple onFiles={onFiles} busy={busy} hint="Drop every client's chart PDF at once. Each file is matched to the client on the bed board by name and date of birth. A file that matches nobody is reported and not stored — nothing is created from a PDF alone." />
      {msg && <div style={{ marginTop:8, fontSize:12.5, color: msg.err ? '#b42318' : '#067647' }}>{msg.err || msg.ok}</div>}
      {results && results.results.some(r => !r.attachment_id) && (
        <div style={{ ...prBox, marginTop:10, borderColor:'#f2c3c3' }}>
          <div style={{ fontWeight:600, fontSize:12.5, color:'#b42318', marginBottom:4 }}>Not stored</div>
          {results.results.filter(r => !r.attachment_id).map((r, i) => <div key={i} style={{ fontSize:12.5 }}>{r.filename} — {r.reason}</div>)}
        </div>)}
      {stays && (
        <table style={{ width:'100%', borderCollapse:'collapse', marginTop:10, fontSize:12.5 }}>
          <thead><tr style={{ background:'#eef1f5' }}>{['Client', 'DOB', 'Admitted', 'LOC', 'Prior record', 'Read', 'Sections applied'].map(h => <th key={h} style={{ textAlign:'left', padding:'6px 8px', fontSize:11.5 }}>{h}</th>)}</tr></thead>
          <tbody>{stays.map(s => (
            <tr key={s.episode_id} style={{ borderBottom:'1px solid #eef1f5' }}>
              <td style={{ padding:'6px 8px' }}>{s.last_name}, {s.first_name}</td><td style={{ padding:'6px 8px' }}>{s.dob || '—'}</td><td style={{ padding:'6px 8px' }}>{s.admit_date || '—'}</td><td style={{ padding:'6px 8px' }}>{s.loc || '—'}</td>
              <td style={{ padding:'6px 8px' }}>{s.attachment_id ? s.filename : <span style={{ color:'#9a6700' }}>none yet</span>}</td>
              <td style={{ padding:'6px 8px' }}>{!s.attachment_id ? '—' : s.status === 'read' ? 'read · ' + s.dx_found + ' dx · ' + s.meds_found + ' meds' : s.status === 'error' ? <span style={{ color:'#b42318' }}>error</span> : 'reading…'}</td>
              <td style={{ padding:'6px 8px' }}>{s.attachment_id ? applied(s) + ' of 6' : '—'}</td>
            </tr>))}</tbody>
        </table>)}
      <div style={{ ...prMuted, marginTop:8 }}>Sections are applied from each client's chart (Records tab → Prior EMR record), by the person who checks them against the document.</div>
    </div>
  );
}

// Facility medication model (config over code): how medications are handled at this facility.
//   self_admin_observed — clients self-administer, staff observe and log the MAR (default)
//   staff_administered  — staff give the dose and log the MAR
//   prescribe_only      — prescriber writes orders/scripts; nothing is administered on site, no MAR (outpatient PHP/IOP)
const MED_MODELS = [
  ['self_admin_observed', 'Self-administration observed', 'Clients take their own medications; staff observe and log each dose on the MAR.'],
  ['staff_administered', 'Staff administered', 'Staff give each dose and log it on the MAR.'],
  ['prescribe_only', 'Prescribe only (no MAR)', 'Providers write orders and prescriptions; medications are not administered on site. The MAR tab is hidden for this facility.'],
];
function MedModelPicker({ api, facilityId, facilityName }) {
  const [cfg, setCfg] = React.useState(null); const [model, setModel] = React.useState(''); const [msg, setMsg] = React.useState(null); const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { let on = true; setMsg(null);
    axios.get(`${api}/api/emr/med-pass/${facilityId}`).then(r => { if (!on) return; setCfg(r.data); setModel(r.data.med_admin_model || 'self_admin_observed'); }).catch(e => on && setMsg({ err: e.response?.data?.error || e.message }));
    return () => { on = false; }; }, [api, facilityId]);
  if (!cfg) return null;
  const save = async () => { setSaving(true); setMsg(null);
    try { const { data } = await axios.put(`${api}/api/emr/med-pass/${facilityId}`, { passes: cfg.passes, frequency_map: cfg.frequency_map, med_window_minutes: cfg.med_window_minutes, med_admin_model: model });
      setCfg(data); setMsg({ ok: 'Saved. Charts at ' + facilityName + ' now follow the "' + (MED_MODELS.find(m => m[0] === model) || [])[1] + '" model.' });
    } catch (e) { setMsg({ err: e.response?.data?.error || e.message }); } finally { setSaving(false); } };
  const cur = MED_MODELS.find(m => m[0] === model) || MED_MODELS[0];
  return (
    <div style={{ background:'#fff', border:'1px solid #e6e9ee', borderRadius:12, padding:16, marginBottom:14 }}>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <strong style={{ fontSize:13 }}>Medication model</strong>
        <select value={model} onChange={e => setModel(e.target.value)} disabled={!cfg.can_edit}
          style={{ padding:'8px 10px', border:'1px solid #d5dbe3', borderRadius:8, fontSize:14, fontFamily:'Inter, system-ui, sans-serif' }}>
          {MED_MODELS.map(m => <option key={m[0]} value={m[0]}>{m[1]}</option>)}
        </select>
        {cfg.can_edit && <button onClick={save} disabled={saving || model === cfg.med_admin_model}
          style={{ padding:'8px 14px', borderRadius:8, border:'none', background:'#0878E8', color:'#fff', fontWeight:600, opacity: (saving || model === cfg.med_admin_model) ? 0.5 : 1 }}>{saving ? 'Saving…' : 'Save model'}</button>}
      </div>
      <div style={{ color:'#5b6b7c', fontSize:12, marginTop:6 }}>{cur[2]}</div>
      {msg && <div style={{ marginTop:8, fontSize:12, color: msg.err ? '#b42318' : '#067647' }}>{msg.err || msg.ok}</div>}
    </div>
  );
}

// ── DISCHARGED CHART FINDER (2026-09-09): permanent way back to any discharged
// chart at an emr_live facility. Server: /api/emr/patients?view=discharged.
function DischargedFinder({ onOpen, facilityFilter, canSeeSandbox }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [derr, setDerr] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/emr/patients`, { params: { view: 'discharged' } })
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDerr('Could not load discharged charts'));
  }, []);
  const s = q.trim().toLowerCase();
  const filt = rows.filter(p =>
    (canSeeSandbox || !/sandbox/i.test(p.facility_name || ''))
    && (!facilityFilter || facilityFilter === 'ALL' || p.facility_name === facilityFilter)
    && (!s
      || (`${p.first_name || ''} ${p.last_name || ''}`).toLowerCase().includes(s)
      || String(p.mrn || '').toLowerCase().includes(s)));
  const dth = { textAlign:'left', padding:'8px 10px', fontSize:10.5, letterSpacing:'1px', textTransform:'uppercase', color:'#8a6b72', borderBottom:'1px solid #e8dcdf' };
  const dtd = { padding:'9px 10px', fontSize:13.5, borderBottom:'1px solid #f1e8ea' };
  const fdate = v => v ? new Date(v).toLocaleDateString('en-US', { month:'numeric', day:'numeric', year:'2-digit', timeZone:'UTC' }) : '-';
  return (
    <div>
      <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
        <h3 style={{ margin:0 }}>Discharged Charts{facilityFilter && facilityFilter !== 'ALL' ? ` — ${facilityFilter}` : ''} ({filt.length})</h3>
        <input style={{ borderRadius:8, padding:'8px 12px', font:'inherit', fontSize:13.5, border:'1px solid #d9c3c9', minWidth:260 }} placeholder="Search name or MRN..." value={q} onChange={e => setQ(e.target.value)} />
      </div>
      {derr && <div style={{ color:'#7f1d1d', marginBottom:10 }}>{derr}</div>}
      <div style={{ background:'#fff', border:'1px solid #e8dcdf', borderRadius:10, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <th style={dth}>Client</th><th style={dth}>MRN</th><th style={dth}>Facility</th>
            <th style={dth}>LOC</th><th style={dth}>Admitted</th><th style={dth}>Discharged</th><th style={dth}>Days</th>
          </tr></thead>
          <tbody>
            {filt.map(p => (
              <tr key={p.episode_id || p.id} onClick={() => onOpen(p.id)} style={{ cursor:'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#faf5f6'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ ...dtd, fontWeight:600 }}>{p.last_name}, {p.first_name}</td>
                <td style={dtd}>{p.mrn || '-'}</td>
                <td style={dtd}>{p.facility_name || '-'}</td>
                <td style={dtd}>{p.loc || '-'}</td>
                <td style={dtd}>{fdate(p.admit_date)}</td>
                <td style={{ ...dtd, fontWeight:600, color:'#7f1d1d' }}>{fdate(p.discharge_date)}</td>
                <td style={dtd}>{p.day_of_stay || '-'}</td>
              </tr>
            ))}
            {!filt.length && !derr && <tr><td style={dtd} colSpan={7}>No discharged charts{s ? ' matching search' : ''}.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Forms admin panel (Layer 2, config-over-code): per-form cosign policy toggle.
// Visible to superuser or manage_clinical_users; a CD flips cosign policy without a vendor call.
function FormsCosignPanel({ api }) {
  const [forms, setForms] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [ferr, setFerr] = useState('');
  const load = useCallback(async () => {
    try { setForms((await axios.get(`${api}/api/emr/forms`)).data); setFerr(''); }
    catch { setFerr('Could not load forms'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (f) => {
    setBusyKey(f.form_key);
    try {
      await axios.patch(`${api}/api/emr/forms/${f.form_key}/cosign`, { cosign_required: !f.cosign_required });
      setForms(fs => fs.map(x => x.form_key === f.form_key ? { ...x, cosign_required: !f.cosign_required } : x));
    } catch (e) { setFerr(e?.response?.data?.error || 'Toggle failed'); }
    setBusyKey(null);
  };
  const cats = ['intake','clinical','treatment_plan','medical','nursing','discharge_planning'];
  const catLabel = { intake: 'Intake & Consents', clinical: 'Clinical', medical: 'Medical', nursing: 'Nursing' };
  const grouped = cats.map(c => [c, forms.filter(f => (f.category || 'clinical') === c)])
    .concat([['other', forms.filter(f => !cats.includes(f.category || 'clinical'))]])
    .filter(([, list]) => list.length);
  const onCount = forms.filter(f => f.cosign_required).length;
  return (
    <div>
      <div style={{ marginBottom: 14, color: '#666', fontSize: 13 }}>
        Forms with cosign ON route to the author's cosigner after signing, regardless of the author's own cosign flag.
        {' '}{onCount} of {forms.length} forms currently require cosign.
        {ferr && <span style={{ color: T.blocking, marginLeft: 10 }}>{ferr}</span>}
      </div>
      {grouped.map(([cat, list]) => (
        <div key={cat} style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '11px 16px', borderBottom: `1px solid ${T.b100}`, fontWeight: 700, fontSize: 13, color: T.ink }}>
            {catLabel[cat] || 'Other'} ({list.length})
          </div>
          {list.map(f => (
            <div key={f.form_key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px', borderBottom: `1px solid ${T.b100}`, fontSize: 13.5 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: T.ink }}>{f.title}</div>
                <div style={{ fontSize: 11.5, color: '#999' }}>{f.form_key} - {f.note_type}</div>
              </div>
              <button onClick={() => toggle(f)} disabled={busyKey === f.form_key}
                style={{ padding: '5px 14px', borderRadius: 14, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                  border: f.cosign_required ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                  background: f.cosign_required ? '#f0fdf4' : '#f8fafc',
                  color: f.cosign_required ? '#166534' : '#9ca3af' }}>
                {busyKey === f.form_key ? '...' : f.cosign_required ? 'COSIGN ON' : 'COSIGN OFF'}
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Facility-aware intake tab (077): facility-scoped packets replace the global
// defaults when they exist; otherwise legacy behavior (dtx/rtc + consents + PHP/IOP by LOC).
function FacilityIntakePackets({ patientId, episode, clientName, clientPassOpen: passProp, setClientPassOpen: setPassProp, facilityName, kind = 'intake' }) {
  const [staffPassOpen, setStaffPassOpen] = useState(false);
  const [localPass, setLocalPass] = useState(false); // discharge instance owns its pass (mig 182)
  const clientPassOpen = setPassProp ? passProp : localPass; const setClientPassOpen = setPassProp || setLocalPass;
  const [rates, setRates] = useState(null);
  const [identity, setIdentity] = useState(null);
  useEffect(() => {
    if (!episode.facility_id) { setRates(null); setIdentity(null); return; }
    axios.get(`${API}/api/emr/facilities/${episode.facility_id}/rates`)
      .then(r => { setRates(r.data.rates); setIdentity(r.data.identity || null); })
      .catch(() => { setRates(null); setIdentity(null); });
  }, [episode.facility_id]);
  const [fp, setFp] = useState(null);
  const facilityId = episode.facility_id || '';
  useEffect(() => {
    if (!facilityId) { setFp({ packets: [], has_facility_packets: false }); return; }
    axios.get(`${API}/api/emr/packets?facilityId=${facilityId}`)
      .then(r => setFp(r.data)).catch(() => setFp({ packets: [], has_facility_packets: false }));
  }, [facilityId]);
  if (!fp) return null;
  // MIGRATED STAY (10/31 ruling): a client whose record came from the previous EMR already has a completed intake. Nothing to redo.
  if (kind === 'intake' && episode.migrated_from) return (
    <div style={{ margin: '8px 18px 12px', padding: '12px 14px', border: '1px solid #D8E3EE', borderRadius: 10, background: '#F8FAFC', fontSize: 13, color: '#22303E', lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600 }}>Intake packet completed in the previous EMR.</span> This client's intake and consents were signed before the move to TCIO. The record is on the Records tab; no intake packet is started here.
    </div>
  );
  const l = String(episode.loc || '').trim().toUpperCase();
  let renderList;
  const wantKinds = kind === 'discharge' ? ['discharge'] : ['intake', 'consents'];
  const facPk = fp.packets.filter(p => p.facility_id && wantKinds.includes(p.kind || 'intake'));
  if (kind === 'discharge') {
    renderList = (facPk.length ? facPk : fp.packets.filter(p => !p.facility_id && p.kind === 'discharge'))
      .filter(p => !p.loc_scope || p.loc_scope.length === 0 || p.loc_scope.some(s => l.startsWith(s)))
      .map(p => ({ key: p.packet_key, heading: p.title }));
    if (!renderList.length) renderList = [{ key: 'discharge_packet', heading: 'Discharge Packet' }];
  } else if (fp.has_facility_packets) {
    renderList = facPk
      .filter(p => !p.loc_scope || p.loc_scope.length === 0 || p.loc_scope.some(s => l.startsWith(s)))
      .map(p => ({ key: p.packet_key, heading: p.title }));
  } else {
    renderList = [
      { key: 'intake_packet_dtx_rtc', heading: 'Intake Packet' },
      { key: 'consents_packet_dtx_rtc', heading: 'Consents Packet' },
    ];
    if (l.startsWith('PHP') || l.startsWith('IOP'))
      renderList.push({ key: 'intake_packet_php_iop', heading: 'PHP/IOP Intake Packet' });
  }
  const passKeys = renderList.map(p => p.key);
  return (
    <>
      <button onClick={() => setStaffPassOpen(true)}
        style={{ margin: '4px 18px 0', background: '#fff', color: '#4479B0', border: '1.5px solid #4479B0',
          borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'block' }}>
        ✓ Staff Completion Pass — complete and sign every client-signed document
      </button>
      <button onClick={() => setClientPassOpen(true)}
        style={{ margin: '4px 18px 10px', background: '#4479B0', color: '#fff', border: 'none',
          borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        ✍ Client Signing Pass — client signs every document in one sitting
      </button>
      <div style={{ margin: '0 18px 10px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {renderList.map(p => (
          <button key={'dl-' + p.key}
            style={{ background: '#fff', color: '#4479B0', border: '1.5px solid #D8E3EE',
              borderRadius: 8, padding: '7px 13px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
            onClick={async (ev) => {
              const b = ev.currentTarget;
              const orig = b.textContent;
              b.textContent = 'Generating\u2026'; b.disabled = true;
              try {
                const tk = localStorage.getItem('tcio_token') || '';
                const r = await fetch(`${API}/api/emr/patients/${patientId}/intake-packet.pdf?packet_key=${p.key}&episode_id=${episode.id}`,
                  { headers: { Authorization: 'Bearer ' + tk } });
                if (!r.ok) { alert('Could not generate the packet PDF (' + r.status + ')'); return; }
                const blob = await r.blob();
                const cd = r.headers.get('Content-Disposition') || '';
                const m = cd.match(/filename="([^"]+)"/);
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = (m && m[1]) || 'intake_packet.pdf';
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 4000);
              } catch (e) { alert('Download failed: ' + e.message); }
              finally { b.textContent = orig; b.disabled = false; }
            }}>Download {p.heading} PDF</button>
        ))}
      </div>
      {staffPassOpen && (
        <StaffCompletionPass patientId={patientId} episodeId={episode.id}
          facilityName={facilityName || episode.facility_name || ''}
          rates={rates} identity={identity}
          packetKeys={passKeys}
          onClose={() => { setStaffPassOpen(false); }} />
      )}
      {clientPassOpen && (
        <ClientSigningPass patientId={patientId} episodeId={episode.id}
          facilityName={facilityName || episode.facility_name || ''}
          rates={rates} identity={identity}
          packetKeys={passKeys}
          clientNameDefault={clientName}
          onClose={() => setClientPassOpen(false)} />
      )}
      {renderList.map(p => (
        <EMRNotes key={p.key} patientId={patientId} facilityName={facilityName || episode.facility_name || ''} rates={rates} identity={identity} categories={kind === 'discharge' ? ['discharge_planning'] : ['intake_packet']}
          heading={p.heading} packetKey={p.key} episodeId={episode.id} />
      ))}
    </>
  );
}
const API = 'https://tcio-api.onrender.com';

// ── Blush tokens (approved 7/25) ─────────────────────────────
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase',
  color:T.inkSoft, fontWeight:600 };
const input = { border:`1px solid ${T.b200}`, borderRadius:8, padding:'8px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box' };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px',
  font:'inherit', fontWeight:600, fontSize:13, cursor:'pointer',
  background: primary ? T.rose : '#fff',
  color: primary ? '#fff' : T.rose,
  border: primary ? 'none' : `1px solid ${T.b300}` });
const locPill = (loc) => <StatusDot label={loc} />;
const th = { ...label, textAlign:'left', padding:'8px 18px' };
const td = { padding:'9px 18px', borderBottom:`1px solid ${T.b100}`, fontSize:13.5 };

// Facility dropdown order: All first, rest alphabetical (zero-hardcode doctrine)
function orderFacilities(names) {
  return [...names].sort((a,b) => a.localeCompare(b));
}

function RoundLevelControl({ episodeId, user }) {
  const [levels, setLevels] = useState([]);
  const [board, setBoard] = useState(null);
  const [editing, setEditing] = useState(false);
  const [levelId, setLevelId] = useState('');
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState('');
  const canChange = true; // BHTs may raise or lower rounds (Chad 11/2); the API enforces who may do what
  const load = useCallback(async () => {
    if (!episodeId) return;
    try {
      const lv = await axios.get(`${API}/api/round-levels`);
      setLevels(lv.data.data || []);
      const ep = await axios.get(`${API}/api/emr/facesheet-episode-round/${episodeId}`).catch(() => null);
      setBoard(ep && ep.data ? ep.data : null);
    } catch {}
  }, [episodeId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!levelId || !reason.trim()) { setMsg('Level and reason are both required.'); return; }
    try {
      await axios.post(`${API}/api/rounds/${episodeId}/level`, { round_level_id: levelId, reason: reason.trim() });
      setMsg(''); setEditing(false); setLevelId(''); setReason(''); await load();
    } catch (e) { setMsg((e.response && e.response.data && e.response.data.error) || 'Change failed'); }
  };
  const cur = board && board.current;
  return (
    <div style={{ padding:'11px 18px', borderBottom:`1px solid ${T.b100}`, background:T.bg,
      display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
      <span style={{ fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:'1px', color:T.inkSoft }}>
        Observation level</span>
      <span style={{ fontWeight:600, color:T.roseDeep }}>
        {cur ? cur.code : '—'}{cur && cur.is_override ? ' (patient-specific)' : cur ? ' (LOC default)' : ''}</span>
      {canChange && !editing &&
        <button style={{ borderRadius:8, padding:'5px 12px', font:'inherit', fontSize:12, fontWeight:600,
          cursor:'pointer', border:`1px solid ${T.b300}`, background:'transparent', color:T.rose }}
          onClick={() => setEditing(true)}>Change</button>}
      {editing && (
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', flex:1, minWidth:280 }}>
          <select style={{ ...input, width:'auto' }} value={levelId} onChange={e => setLevelId(e.target.value)}>
            <option value="">Level…</option>
            {levels.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
          </select>
          <input style={{ ...input, flex:1, minWidth:180 }} placeholder="Reason (required — audited)"
            value={reason} onChange={e => setReason(e.target.value)} />
          <button style={{ borderRadius:8, padding:'7px 14px', font:'inherit', fontSize:12.5, fontWeight:600,
            cursor:'pointer', border:'none', background:T.rose, color:'#fff' }} onClick={save}>Save</button>
          <button style={{ borderRadius:8, padding:'7px 12px', font:'inherit', fontSize:12.5, cursor:'pointer',
            border:`1px solid ${T.b300}`, background:'transparent', color:T.ink }}
            onClick={() => { setEditing(false); setMsg(''); }}>Cancel</button>
        </div>
      )}
      {msg && <div style={{ width:'100%', color:T.blocking, fontSize:12.5 }}>{msg}</div>}
      {board && board.history && board.history.length > 0 && (
        <div style={{ width:'100%', fontSize:12, color:T.inkSoft }}>
          {board.history.map(h => (
            <div key={h.id} style={{ marginTop:3 }}>
              {h.code} · {new Date(h.started_at).toLocaleDateString('en-US')} · {h.set_by_name}: {h.reason}
              {h.ended_at ? '' : ' (current)'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function MarDay({ patientId, episodeId, active }) {
  const [date, setDate] = useState(new Date().toLocaleDateString('en-CA', { timeZone:'America/Los_Angeles' }));
  const [data, setData] = useState(null);
  const [xerr, setXerr] = useState('');
  const [holdFor, setHoldFor] = useState(null);
  const [holdReason, setHoldReason] = useState('');
  const [prnFor, setPrnFor] = useState(null);
  const [sigFor, setSigFor] = useState(null);
  const [sigName, setSigName] = useState('');
  const [prnNote, setPrnNote] = useState('');
  const [tab, setTab] = useState('due');
  const [sigPass, setSigPass] = useState(null); // { slot, clientSig, staffPin, err, enroll, enrollSig, enrollPin }
  const load = async (d) => {
    try { setData((await axios.get(`${API}/api/emr/patients/${patientId}/mar`, { params:{ date:d, ...(episodeId ? { scope_episode: episodeId } : {}) } })).data); setXerr(''); }
    catch { setXerr('Could not load MAR'); }
  };
  useEffect(() => { if (patientId && active) load(date); }, [patientId, date, active]);
  const shiftDay = (n) => {
    const d = new Date(Date.parse(date + 'T12:00:00') + n * 86400000);
    setDate(d.toLocaleDateString('en-CA'));
  };
  const MAR_REASONS = {
    refused: ['Patient refused', 'Patient asleep - refused on wake', 'Patient off-site', 'Other'],
    held: ['Provider hold order', 'Vitals outside parameters', 'Clinical judgment - provider notified', 'Other'],
    missed: ['Patient off-site', 'Patient asleep', 'Medication unavailable', 'Other'],
  };
  const [reasonFor, setReasonFor] = useState(null);
  const [reasonStatus, setReasonStatus] = useState('');
  const [reasonPick, setReasonPick] = useState('');
  const [reasonText, setReasonText] = useState('');
  const openReason = (s, status) => {
    setReasonFor(`${s.order_id}|${s.time}`); setReasonStatus(status); setReasonPick(''); setReasonText('');
  };
  const [winFor, setWinFor] = useState(null); // { slot, status, extra, msg, approval }
  const [vitalsGate, setVitalsGate] = useState(null); // { kind:'required'|'breach', slot, status, extra, msg, breaches, vitals, takeOpen, overrideText, approval }
  const log = async (slot, status, extra) => {
    if (!(extra && extra.staff_pin)) {
      setSigPass({ slot, status, extra: extra || {}, clientSig: null, staffPin: '' }); return;
    }
    try {
      await axios.post(`${API}/api/emr/mar-entries`, {
        order_id: slot.order_id, patient_id: patientId, scheduled_date: date,
        scheduled_time: slot.time || null, status,
        amount: slot.amount, strength: slot.strength, unit: slot.unit, ...(extra || {}) });
      setHoldFor(null); setHoldReason(''); setPrnFor(null); setPrnNote(''); setWinFor(null); load(date);
    } catch (e) {
      const d = (e.response && e.response.data) || {};
      if (d.window_exceeded) { setWinFor({ slot, status, extra: extra || {}, msg: d.error, approval: '' }); return; }
      if (d.vitals_required) { setVitalsGate({ kind:'required', slot, status, extra: extra || {}, msg: d.error, takeOpen: false, overrideText: '' }); return; }
      if (d.vitals_breach) { setVitalsGate({ kind:'breach', slot, status, extra: extra || {}, msg: d.error, breaches: d.breaches || [], vitals: d.vitals || null, approval: '' }); return; }
      setXerr(d.error || 'Log failed');
    }
  };
  if (!data) return null;
  const selfAdmin = data.med_admin_model === 'self_admin_observed';
  const takeLabel = selfAdmin ? 'Self-admin observed' : 'Given';
  const takeStatus = selfAdmin ? 'self_administered_observed' : 'taken';
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone:'America/Los_Angeles' });
  const isToday = date === todayStr;
  const nowHM = new Date().toLocaleTimeString('en-GB', { timeZone:'America/Los_Angeles', hour:'2-digit', minute:'2-digit' });
  const nowMin = (() => { const [h, m] = nowHM.split(':').map(Number); return h * 60 + m; })();
  // Ticket cad23201 (9/24): '8:00 pm' used to parse as 8:00 - the am/pm was dropped, so every PM dose read as late all day.
  const slotMin = (t) => { const mt = String(t || '').trim().toLowerCase().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/); if (!mt) return 0;
    let h = Number(mt[1]); const m = Number(mt[2] || 0); const ap = (mt[3] || '').replace(/\./g, '');
    if (ap === 'pm' && h < 12) h += 12; if (ap === 'am' && h === 12) h = 0; return h * 60 + m; };
  const slotState = (sl) => {
    if (sl.entry) return ['refused','held','missed'].includes(sl.entry.status) ? 'exception' : 'completed';
    if (!isToday) return date < todayStr ? 'overdue' : 'upcoming';
    const dm = nowMin - slotMin(sl.time);
    if (dm >= 30) return 'overdue';
    if (dm >= -15) return 'due';
    return 'upcoming';
  };
  const decorated = data.slots.map(sl => ({ ...sl, _st: slotState(sl),
    _est: (sl.on_hand != null && sl.doses_per_day > 0 && Number(sl.amount) > 0)
      ? Math.floor((Number(sl.on_hand) / (sl.doses_per_day * Number(sl.amount))) * 10) / 10 : null }));
  const counts = {
    scheduled: decorated.length,
    due: decorated.filter(x => x._st === 'due' || x._st === 'overdue').length,
    completed: decorated.filter(x => x._st === 'completed').length,
    upcoming: decorated.filter(x => x._st === 'upcoming').length,
    exceptions: decorated.filter(x => x._st === 'exception').length,
  };
  const visibleSlots = tab === 'all' ? decorated
    : tab === 'due' ? decorated.filter(x => x._st === 'due' || x._st === 'overdue')
    : tab === 'completed' ? decorated.filter(x => x._st === 'completed')
    : tab === 'exceptions' ? decorated.filter(x => x._st === 'exception')
    : [];
  const entryChip = (en) => {
    const map = { taken:['#e8f5e9','#1b5e20','GIVEN'], self_administered_observed:['#e8f5e9','#1b5e20','SELF-ADMIN OBS'],
      refused:['#fdecea','#b30000','REFUSED'], held:['#fff8e1','#8d6e00','HELD'],
      missed:['#eceff1','#546e7a','MISSED'], prn_given:['#e3f2fd','#0d47a1','PRN GIVEN'] };
    const [b, c, l] = map[en.status];
    return <span style={{ background:b, color:c, borderRadius:99, padding:'1px 10px', fontSize:11, fontWeight:600 }}>
      {l} · {new Date(en.recorded_at).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })} · {en.recorded_by_name}</span>;
  };
  const holdBadge = (s) => {
    const p = [];
    if (s.hold_sbp_min) p.push(`SBP<${s.hold_sbp_min}`);
    if (s.hold_dbp_min) p.push(`DBP<${s.hold_dbp_min}`);
    if (s.hold_pulse_min) p.push(`P<${s.hold_pulse_min}`);
    if (s.hold_if_sedated) p.push('sedation');
    return p.length ? <span style={{ background:'#fff8e1', color:'#8d6e00', border:'1px solid #e6c200',
      borderRadius:6, padding:'1px 8px', fontSize:11, fontWeight:600 }}>HOLD: {p.join(' · ')}</span> : null;
  };
  return (
    <div style={{ ...card, marginBottom:16 }}>
      <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}`,
        display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <strong>MAR</strong>
        <span style={{ fontSize:11.5, color:T.inkSoft }}>
          {selfAdmin ? 'Self-administration with staff observation' : 'Staff-administered'}</span>
        <span style={{ marginLeft:'auto', display:'flex', gap:6, alignItems:'center' }}>
          <button style={btn(false)} onClick={() => shiftDay(-1)}>‹</button>
          <input type="date" style={input} value={date} onChange={e => setDate(e.target.value)} />
          <button style={btn(false)} onClick={() => shiftDay(1)}>›</button>
        </span>
      </div>
      <div style={{ padding:18 }}>
        {xerr && <div style={{ color:T.blocking, fontSize:13, marginBottom:8 }}>{xerr}</div>}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
          {[['Scheduled', counts.scheduled, T.ink], ['Due Now', counts.due, '#b30000'],
            ['Completed', counts.completed, '#1b5e20'], ['Upcoming', counts.upcoming, '#8d6e00'],
            ['Exceptions', counts.exceptions, '#546e7a']].map(([l, n, c]) => (
            <div key={l} style={{ border:`1px solid ${T.b100}`, borderRadius:8, padding:'5px 12px', minWidth:72 }}>
              <div style={{ fontSize:17, fontWeight:800, color:c, lineHeight:1.2 }}>{n}</div>
              <div style={{ fontSize:10.5, color:T.inkSoft, fontWeight:600 }}>{l}</div>
            </div>))}
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
          {[['due','Due Now'], ['all','All'], ['completed','Completed'], ['prn','PRN'], ['exceptions','Exceptions']].map(([k, l]) => (
            <button key={k} style={btn(tab === k)} onClick={() => setTab(k)}>{l}</button>))}
        </div>
        {sigPass && (
          <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', zIndex:1300,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#fff', borderRadius:12, width:'min(560px, 96vw)', maxHeight:'92vh', overflowY:'auto', padding:'18px 20px' }}>
              <div style={{ fontWeight:800, fontSize:15, marginBottom:3 }}>{({ self_administered_observed:'Observe Self-Administration', taken:'Medication given', prn_given:'PRN dose given', refused:'Dose refused', held:'Dose held', missed:'Dose missed' })[sigPass.status || takeStatus] || 'Sign MAR entry'}</div>
              <div style={{ fontSize:13, color:T.inkSoft, marginBottom:12 }}>
                {sigPass.slot.medication_name} · {sigPass.slot.amount} {unitTag(sigPass.slot.unit)} {sigPass.slot.strength}
                {sigPass.slot.rx_number ? ` · Rx ${sigPass.slot.rx_number}` : ''}
                {sigPass.slot.time ? ` · Scheduled ${sigPass.slot.time}` : ''}
              </div>
              {['taken','self_administered_observed','prn_given'].includes(sigPass.status || takeStatus) && (<>
              <div style={{ fontSize:12.5, fontWeight:700 }}>1. Client signs</div>
              <div style={{ fontSize:11.5, color:T.inkSoft, marginBottom:5 }}>{(sigPass.status || takeStatus) === 'self_administered_observed' ? '"I self-administered this medication as documented above."' : '"I received this medication as documented above."'}</div>
              <SignaturePad height={110} onChange={v => setSigPass(sp => sp ? { ...sp, clientSig: v } : sp)} />
              </>)}
              <div style={{ fontSize:12.5, fontWeight:700, marginTop:12 }}>{['taken','self_administered_observed','prn_given'].includes(sigPass.status || takeStatus) ? '2. Staff PIN — ' : 'Staff PIN — '}{(sigPass.status || takeStatus) === 'self_administered_observed' ? 'observed by' : 'recorded by'}</div>
              <div style={{ fontSize:11.5, color:T.inkSoft, marginBottom:5 }}>Your enrolled signature is applied on PIN entry.</div>
              {sigPass.enroll ? (
                <div style={{ border:`1px solid ${T.b100}`, borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:12, fontWeight:700, marginBottom:4 }}>One-time enrollment: draw your signature and set your 4-digit PIN</div>
                  <SignaturePad height={100} onChange={v => setSigPass(sp => sp ? { ...sp, enrollSig: v } : sp)} />
                  <input style={{ ...input, width:120, marginTop:8 }} placeholder="Set 4-digit PIN" maxLength={4} type="password" inputMode="numeric"
                    value={sigPass.enrollPin || ''} onChange={e => setSigPass(sp => sp ? { ...sp, enrollPin: e.target.value.replace(/\D/g, '') } : sp)} />
                  <button style={{ ...btn(true), marginLeft:8 }} disabled={!sigPass.enrollSig || (sigPass.enrollPin || '').length !== 4}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/api/emr/signature/enroll`, { image_base64: sigPass.enrollSig, pin: sigPass.enrollPin });
                        setSigPass(sp => sp ? { ...sp, enroll: false, staffPin: sp.enrollPin, err: 'Enrolled - hit Complete to finish.' } : sp);
                      } catch (e2) { setSigPass(sp => sp ? { ...sp, err: (e2.response && e2.response.data && e2.response.data.error) || 'Enroll failed' } : sp); }
                    }}>Enroll</button>
                </div>
              ) : (
                <input style={{ ...input, width:140 }} placeholder="4-digit PIN" maxLength={4} type="password" inputMode="numeric"
                  value={sigPass.staffPin || ''} onChange={e => setSigPass(sp => sp ? { ...sp, staffPin: e.target.value.replace(/\D/g, '') } : sp)} />
              )}
              {sigPass.winNeed && (
                <div style={{ marginTop:8 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#8d6e00', marginBottom:4 }}>Outside med window — document prescriber contact/approval:</div>
                  <input style={{ ...input, width:'100%' }} placeholder="e.g. Dr. Small contacted 9:15 PM, approved late dose"
                    value={sigPass.winAppr || ''} onChange={e => setSigPass(sp => sp ? { ...sp, winAppr: e.target.value } : sp)} />
                </div>
              )}
              {sigPass.err && <div style={{ fontSize:12, color:T.blocking, marginTop:6 }}>{sigPass.err}</div>}
              <div style={{ display:'flex', gap:8, marginTop:14, justifyContent:'flex-end' }}>
                <button style={btn(false)} onClick={() => setSigPass(null)}>Cancel</button>
                <button style={btn(true)} disabled={sigPass.saving || (['taken','self_administered_observed','prn_given'].includes(sigPass.status || takeStatus) && !sigPass.clientSig) || (sigPass.staffPin || '').length !== 4}
                  onClick={async () => {
                    if (sigPass.saving) return;
                    setSigPass(sp => sp ? { ...sp, saving: true } : sp);
                    try {
                      await axios.post(`${API}/api/emr/mar-entries`, {
                        order_id: sigPass.slot.order_id, patient_id: patientId, scheduled_date: date,
                        scheduled_time: sigPass.slot.time || null, status: sigPass.status || takeStatus,
                        amount: sigPass.slot.amount, strength: sigPass.slot.strength, unit: sigPass.slot.unit,
                        ...(sigPass.extra || {}), patient_signature: sigPass.clientSig || null, staff_pin: sigPass.staffPin,
                        ...(sigPass.winAppr ? { prescriber_approval: sigPass.winAppr } : {}) });
                      setSigPass(null); setHoldFor(null); setHoldReason(''); setPrnFor(null); setPrnNote(''); setWinFor(null); setReasonFor(null); load(date);
                    } catch (e2) {
                      const d2 = (e2.response && e2.response.data) || {};
                      if (d2.enroll_required) setSigPass(sp => sp ? { ...sp, enroll: true, err: '' } : sp);
                      else if (d2.window_exceeded) setSigPass(sp => sp ? { ...sp, winNeed: true, err: d2.error } : sp);
                      else setSigPass(sp => sp ? { ...sp, err: d2.error || 'Failed', staffPin: '' } : sp);
                      setSigPass(sp => sp ? { ...sp, saving: false } : sp);
                    }
                  }}>{sigPass.saving ? 'Saving…' : 'Complete — signed'}</button>
              </div>
            </div>
          </div>
        )}
        {vitalsGate && (
          <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', zIndex:1300,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#fff', borderRadius:12, width:'min(560px, 96vw)', maxHeight:'92vh', overflowY:'auto',
              padding:'18px 20px', borderTop:'5px solid #e6a700' }}>
              <div style={{ fontWeight:800, fontSize:15, marginBottom:3 }}>
                {vitalsGate.kind === 'required' ? 'Vitals Needed Before This Dose' : 'Hold Parameters Breached'}</div>
              <div style={{ fontSize:13, color:T.inkSoft, marginBottom:8 }}>
                {vitalsGate.slot.medication_name} · {vitalsGate.slot.amount} {unitTag(vitalsGate.slot.unit)} {vitalsGate.slot.strength}
                {vitalsGate.slot.time ? ` · Scheduled ${vitalsGate.slot.time}` : ''}</div>
              <div style={{ background:'#fff8e1', border:'1px solid #e6c200', borderRadius:8,
                padding:'9px 12px', fontSize:12.5, color:'#6d5500', marginBottom:12 }}>{vitalsGate.msg}</div>
              {vitalsGate.kind === 'required' ? (
                <>
                  {episodeId && (
                    <button style={{ ...btn(true), marginBottom:12 }} onClick={() => setVitalsGate(g => g ? { ...g, takeOpen: true } : g)}>
                      ❤ Take Vitals Now</button>)}
                  {vitalsGate.takeOpen && episodeId && (
                    <VitalsModal episodeId={episodeId}
                      onClose={() => setVitalsGate(g => g ? { ...g, takeOpen: false } : g)}
                      onSaved={() => {
                        const g = vitalsGate; setVitalsGate(null);
                        log(g.slot, g.status, g.extra);
                      }} />)}
                  <div style={{ fontSize:12.5, fontWeight:700, marginBottom:4 }}>Or document why vitals could not be obtained</div>
                  <input style={{ ...input, width:'100%', marginBottom:8 }} placeholder="e.g., Patient refused vitals check"
                    value={vitalsGate.overrideText}
                    onChange={e => setVitalsGate(g => g ? { ...g, overrideText: e.target.value } : g)} />
                  <div style={{ display:'flex', gap:8 }}>
                    <button style={btn(true)} disabled={!(vitalsGate.overrideText || '').trim()}
                      onClick={() => { const g = vitalsGate; setVitalsGate(null);
                        log(g.slot, g.status, { ...g.extra, vitals_override: g.overrideText.trim() }); }}>
                      Record Dose With Documented Reason</button>
                    <button style={btn(false)} onClick={() => setVitalsGate(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  {vitalsGate.vitals && (
                    <div style={{ fontSize:12.5, marginBottom:10 }}>
                      Latest reading {new Date(vitalsGate.vitals.taken_at).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}:
                      {vitalsGate.vitals.systolic != null ? ` BP ${vitalsGate.vitals.systolic}/${vitalsGate.vitals.diastolic}` : ''}
                      {vitalsGate.vitals.pulse != null ? ` · P ${vitalsGate.vitals.pulse}` : ''}</div>)}
                  <div style={{ fontSize:12.5, fontWeight:700, marginBottom:4 }}>Prescriber contacted — document who approved giving this dose</div>
                  <input style={{ ...input, width:'100%', marginBottom:8 }} placeholder="e.g., Dr. Small approved by phone 4:35 PM"
                    value={vitalsGate.approval}
                    onChange={e => setVitalsGate(g => g ? { ...g, approval: e.target.value } : g)} />
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button style={btn(true)} disabled={!(vitalsGate.approval || '').trim()}
                      onClick={() => { const g = vitalsGate; setVitalsGate(null);
                        log(g.slot, g.status, { ...g.extra, prescriber_approval_vitals: g.approval.trim() }); }}>
                      Give — Prescriber Approved</button>
                    <button style={btn(false)}
                      onClick={() => { const g = vitalsGate; setVitalsGate(null); openReason(g.slot, 'held'); }}>
                      Hold This Dose Instead</button>
                    <button style={btn(false)} onClick={() => setVitalsGate(null)}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {winFor && (
          <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.55)', zIndex:1100,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
            onClick={() => setWinFor(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:14, padding:20, maxWidth:440, width:'100%',
              boxShadow:'0 24px 64px rgba(15,23,42,0.35)' }}>
              <div style={{ fontWeight:800, fontSize:15, marginBottom:8 }}>Outside Medication Window</div>
              <div style={{ fontSize:13, color:'#475569', marginBottom:12 }}>{winFor.msg}</div>
              <div style={{ fontSize:12, fontWeight:700, marginBottom:4 }}>Prescriber contact / approval (required):</div>
              <input style={{ ...input, width:'100%' }} placeholder="e.g. Dr. Small contacted 9:15 PM, approved late dose"
                value={winFor.approval} onChange={e => setWinFor(w => w ? { ...w, approval: e.target.value } : w)} />
              <div style={{ display:'flex', gap:8, marginTop:14, justifyContent:'flex-end' }}>
                <button style={btn(false)} onClick={() => setWinFor(null)}>Cancel</button>
                <button style={btn(true)} disabled={!winFor.approval.trim()}
                  onClick={() => log(winFor.slot, winFor.status, { ...winFor.extra, prescriber_approval: winFor.approval.trim() })}>
                  Record with Approval</button>
              </div>
            </div>
          </div>
        )}
        {!data.slots.length && !data.prn.length &&
          <div style={{ color:T.inkSoft }}>No active medication schedule for this date.</div>}
        {data.slots.length > 0 && visibleSlots.length === 0 && tab !== 'prn' &&
          <div style={{ color:T.inkSoft, fontSize:13, padding:'6px 0' }}>
            {tab === 'due' ? 'Nothing due right now — all caught up ✓' : 'Nothing in this view.'}</div>}
        {visibleSlots.map((s, i) => (
          <div key={i} style={{ padding:'10px 8px', borderBottom:`1px solid ${T.b100}`,
            background: (s._st === 'overdue' || s._st === 'due') ? '#fff5f5' : 'transparent',
            opacity: s._st === 'completed' ? 0.65 : 1, borderRadius:8,
            display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:13, minWidth:56,
              color: (s._st === 'overdue' || s._st === 'due') ? '#b30000' : s._st === 'completed' ? '#1b5e20' : T.ink }}>
              {s.time}{s._st === 'overdue' && isToday ? ` +${nowMin - slotMin(s.time)}m` : ''}</span>
            <strong>{s.medication_name}</strong>
            {s.rx_number && <span style={{ fontFamily:'monospace', fontSize:11, color:T.inkSoft, background:'#f5f5f5', borderRadius:6, padding:'1px 7px', marginLeft:6 }}>Rx {s.rx_number}</span>}
            <span style={{ fontSize:12.5, color:T.inkSoft }}>{s.amount} {unitTag(s.unit)} {s.strength}</span>
            {s.ordered_by_name && <span style={{ fontSize:11.5, color:T.inkSoft }}>· {s.ordered_by_name}</span>}
            {s.on_hand != null && <span style={{ fontSize:11.5, fontWeight: s._est != null && s._est < 3 ? 700 : 400,
              color: s._est != null && s._est < 3 ? '#b26a00' : T.inkSoft }}>
              · On hand {s.on_hand}{s._est != null ? ` · ~${s._est}d supply` : ''}</span>}
            {s.controlled_schedule && <span style={{ background:'#ede7f6', color:'#4527a0', borderRadius:6,
              padding:'1px 8px', fontSize:11, fontWeight:600 }}>{s.controlled_schedule}</span>}
            {holdBadge(s)}
            <span style={{ marginLeft:'auto', display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
              {s.entry ? entryChip(s.entry) : reasonFor === `${s.order_id}|${s.time}` ? (
                <>
                  <select style={{ ...input, width:240 }} value={reasonPick}
                    onChange={e => setReasonPick(e.target.value)}>
                    <option value="">— Reason (required, DHCS/TJC) —</option>
                    {(MAR_REASONS[reasonStatus] || []).map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {reasonPick === 'Other' &&
                    <input style={{ ...input, width:180 }} placeholder="Describe reason"
                      value={reasonText} onChange={e => setReasonText(e.target.value)} />}
                  <button style={btn(true)} disabled={!reasonPick || (reasonPick === 'Other' && !reasonText.trim())}
                    onClick={() => {
                      const reason = reasonPick === 'Other' ? reasonText.trim() : reasonPick;
                      log(s, reasonStatus, reasonStatus === 'held' ? { hold_reason: reason } : { note: reason });
                      setReasonFor(null); setReasonPick(''); setReasonText('');
                    }}>Confirm {reasonStatus}</button>
                  <button style={btn(false)} onClick={() => { setReasonFor(null); setReasonPick(''); setReasonText(''); }}>×</button>
                </>
              ) : (
                <>
                  <button style={btn(true)} onClick={() => log(s, takeStatus)}>
                    {selfAdmin ? 'Observe Self-Administration' : takeLabel}</button>
                  <button style={btn(false)} onClick={() => openReason(s, 'refused')}>Refused</button>
                  <button style={btn(false)} onClick={() => openReason(s, 'held')}>Hold</button>
                  <button style={btn(false)} onClick={() => openReason(s, 'missed')}>Missed</button>
                </>
              )}
            </span>
          </div>
        ))}
        {(tab === 'all' || tab === 'prn') && data.prn.length > 0 &&
          <div style={{ fontSize:12.5, fontWeight:600, margin:'14px 0 4px' }}>PRN available:</div>}
        {(tab === 'all' || tab === 'prn') && data.prn.map((p, i) => (
          <div key={i} style={{ padding:'10px 0', borderBottom:`1px solid ${T.b100}` }}>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <strong>{p.medication_name}</strong>
              {p.rx_number && <span style={{ fontFamily:'monospace', fontSize:11, color:T.inkSoft, background:'#f5f5f5', borderRadius:6, padding:'1px 7px', marginLeft:6 }}>Rx {p.rx_number}</span>}
              {p.controlled_schedule && <span style={{ background:'#ede7f6', color:'#4527a0', borderRadius:6,
                padding:'1px 8px', fontSize:11, fontWeight:600 }}>{p.controlled_schedule}</span>}
              {(p.segments || []).map((sg, j) =>
                <span key={j} style={{ fontSize:12.5, color:T.inkSoft }}>
                  {sg.amount} {unitTag(sg.unit)} {sg.strength} {sg.frequency} PRN</span>)}
              {holdBadge(p)}
              <span style={{ marginLeft:'auto' }}>
                {prnFor === p.order_id ? (
                  <span style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <input style={{ ...input, width:230 }} placeholder="Justification (required)"
                      value={prnNote} onChange={e => setPrnNote(e.target.value)} />
                    <button style={btn(true)} disabled={!prnNote.trim()}
                      onClick={() => log({ order_id:p.order_id,
                        amount:(p.segments[0]||{}).amount, strength:(p.segments[0]||{}).strength,
                        unit:(p.segments[0]||{}).unit, time:null }, 'prn_given', { note: prnNote })}>Log PRN dose</button>
                    <button style={btn(false)} onClick={() => { setPrnFor(null); setPrnNote(''); }}>×</button>
                  </span>
                ) : <button style={btn(false)} onClick={() => setPrnFor(p.order_id)}>+ PRN dose</button>}
              </span>
            </div>
            {p.justification && <div style={{ fontSize:12, color:T.inkSoft, marginTop:3 }}>For: {p.justification}</div>}
            {(p.entries || []).map(en => (
              <div key={en.id} style={{ marginTop:5, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                {entryChip(en)}
                {en.note && <span style={{ fontSize:12, color:T.inkSoft }}>{en.note}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const SEVERITY_COLORS = { anaphylaxis:'#b30000', severe:'#c62828', moderate:'#e65100', mild:'#8d6e63', unknown:'#757575' };

function MrnText({ patientId, facilityId }) {
  const [mrn, setMrn] = useState(null);
  useEffect(() => {
    if (!patientId) return;
    setMrn(null);
    axios.get(`${API}/api/emr/patients/${patientId}/mrn`, { params: facilityId ? { facility_id: facilityId } : {} })
      .then(r => setMrn((r.data && r.data.mrn) || '\u2014'))
      .catch(() => setMrn('\u2014'));
  }, [patientId, facilityId]);
  return <>MRN {mrn || '\u2026'}</>;
}

function AllergyBanner({ patientId, active, compact }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [aerr, setAerr] = useState('');
  const load = async () => {
    try { setRows((await axios.get(`${API}/api/emr/patients/${patientId}/allergies`)).data || []); setAerr(''); }
    catch { setRows([]); setAerr('Could not load allergies'); }
  };
  useEffect(() => { if (patientId && active) load(); }, [patientId, active]);
  const save = async (nkda) => {
    try {
      await axios.post(`${API}/api/emr/patients/${patientId}/allergies`,
        nkda ? { is_nkda: true } : form);
      setForm(null); load();
    } catch (e) { setAerr((e.response && e.response.data && e.response.data.error) || 'Save failed'); }
  };
  if (rows === null) return null;
  const nkda = rows.find(r => r.is_nkda);
  const real = rows.filter(r => !r.is_nkda);
  const bg = real.length ? '#fdecea' : nkda ? '#e8f5e9' : '#f2f2f2';
  const fg = real.length ? '#b30000' : nkda ? '#1b5e20' : T.inkSoft;
  if (compact) {
    const chipText = real.length
      ? '⚠ ' + real.map(r => r.allergen + (r.severity ? ' · ' + r.severity : '')).join(', ')
      : nkda ? 'NKDA' : 'Allergies —';
    const subtle = !real.length;
    return (
      <span style={{ position:'relative', display:'inline-block' }}>
        <span onClick={() => setForm(form ? null : { allergen:'', allergen_type:'drug', reaction:'', severity:'moderate' })}
          title={real.length ? real.map(r => [r.allergen, r.severity, r.reaction].filter(Boolean).join(' — ')).join(' | ') : ''}
          style={{ background: subtle ? 'transparent' : bg, color: subtle ? T.inkSoft : fg,
            border:`1px solid ${subtle ? T.b200 : fg + '55'}`, borderRadius:99,
            padding:'2px 11px', fontSize:12, fontWeight: subtle ? 600 : 800,
            cursor:'pointer', whiteSpace:'nowrap', opacity: subtle ? 0.7 : 1 }}>
          {chipText}</span>
        {form && (
          <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, zIndex:40, background:'#fff',
            border:`1px solid ${T.b200}`, borderRadius:10, boxShadow:'0 6px 20px rgba(0,0,0,0.15)',
            padding:12, display:'grid', gap:8, width:230 }}>
            {aerr && <div style={{ color:T.blocking, fontSize:12 }}>{aerr}</div>}
            <input style={input} placeholder="Allergen"
              value={form.allergen} onChange={e => setForm({ ...form, allergen:e.target.value })} />
            <select style={input} value={form.allergen_type}
              onChange={e => setForm({ ...form, allergen_type:e.target.value })}>
              {['drug','food','environmental','other'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input style={input} placeholder="Reaction (e.g. hives)"
              value={form.reaction} onChange={e => setForm({ ...form, reaction:e.target.value })} />
            <select style={input} value={form.severity}
              onChange={e => setForm({ ...form, severity:e.target.value })}>
              {['mild','moderate','severe','anaphylaxis','unknown'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style={{ display:'flex', gap:6 }}>
              <button style={btn(true)} onClick={() => save(false)} disabled={!form.allergen.trim()}>Save</button>
              {!nkda && !real.length && <button style={btn(false)} onClick={() => save(true)}>NKDA</button>}
              <button style={btn(false)} onClick={() => setForm(null)}>Cancel</button>
            </div>
          </div>
        )}
      </span>
    );
  }
  return (
    <div style={{ ...card, marginBottom:14, background:bg, border:`1px solid ${fg}33` }}>
      <div style={{ padding:'11px 18px', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <strong style={{ color:fg, fontSize:13, letterSpacing:0.3 }}>
          {real.length ? 'ALLERGIES' : nkda ? 'NKDA' : 'ALLERGIES NOT ASSESSED'}
        </strong>
        {real.map(r => (
          <span key={r.id} title={[r.reaction, r.severity].filter(Boolean).join(' — ')}
            style={{ background:'#fff', border:`1px solid ${SEVERITY_COLORS[r.severity] || fg}`,
              color:SEVERITY_COLORS[r.severity] || fg, borderRadius:99, padding:'2px 11px',
              fontSize:12.5, fontWeight:600 }}>
            {r.allergen}{r.severity ? ` · ${r.severity}` : ''}{r.reaction ? ` · ${r.reaction}` : ''}
          </span>
        ))}
        {!real.length && nkda && <span style={{ color:fg, fontSize:12.5 }}>No Known Drug Allergies — confirmed</span>}
        {!real.length && !nkda && <span style={{ fontSize:12.5, color:T.inkSoft }}>Not yet asked / recorded</span>}
        <span style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          {!nkda && !real.length &&
            <button style={btn(false)} onClick={() => save(true)}>Record NKDA</button>}
          <button style={btn(false)} onClick={() =>
            setForm(form ? null : { allergen:'', allergen_type:'drug', reaction:'', severity:'moderate' })}>
            {form ? 'Cancel' : '+ Add allergy'}</button>
        </span>
      </div>
      {aerr && <div style={{ padding:'0 18px 9px', color:T.blocking, fontSize:12.5 }}>{aerr}</div>}
      {form && (
        <div style={{ padding:'0 18px 14px', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <input style={{ ...input, width:170 }} placeholder="Allergen"
            value={form.allergen} onChange={e => setForm({ ...form, allergen:e.target.value })} />
          <select style={{ ...input, width:130 }} value={form.allergen_type}
            onChange={e => setForm({ ...form, allergen_type:e.target.value })}>
            {['drug','food','environmental','other'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input style={{ ...input, width:170 }} placeholder="Reaction (e.g. hives)"
            value={form.reaction} onChange={e => setForm({ ...form, reaction:e.target.value })} />
          <select style={{ ...input, width:130 }} value={form.severity}
            onChange={e => setForm({ ...form, severity:e.target.value })}>
            {['mild','moderate','severe','anaphylaxis','unknown'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button style={btn(true)} onClick={() => save(false)}>Save</button>
        </div>
      )}
    </div>
  );
}

// UNIT AFTER QUANTITY (Karlee, ticket a235211b, 11/2): orders and MAR read "Trazodone 1 TAB 50 mg QHS" — quantity, unit, strength.
const unitTag = (u) => { const m = { tablet:'TAB', capsule:'CAP', 'ml (liquid)':'mL', solution:'mL', suspension:'mL', injection:'INJ', patch:'PATCH', inhaler:'PUFF', 'nasal spray':'SPRAY', drops:'DROP', film:'FILM', lozenge:'LOZ', suppository:'SUPP', powder:'PKT', unit:'UNIT' }; const k = String(u || '').trim().toLowerCase(); return k ? (m[k] || k.toUpperCase()) : ''; };
const EMPTY_SEG = () => ({ day_from:'', day_to:'', frequency:'', is_prn:false, amount:'1', strength:'', unit:'tablet', times:'', times_source:'prescriber', freq_other:false });

function ShiftCountPanel({ facilityId, active }) {
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(false);
  const [shiftId, setShiftId] = useState('');
  const [lines, setLines] = useState({});
  const [witness, setWitness] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [hist, setHist] = useState(null);
  const load = async () => {
    try { setCtx((await axios.get(`${API}/api/emr/facilities/${facilityId}/controlled-count-context`)).data); setErr(''); }
    catch { setErr('Could not load count context'); }
  };
  useEffect(() => { if (facilityId && active) { load(); setResult(null); setHist(null); setOpen(false); } }, [facilityId, active]);
  if (!ctx || !ctx.meds || !ctx.meds.length) return null;
  const setLine = (id, patch) => setLines(l => ({ ...l, [id]: { ...(l[id] || {}), ...patch } }));
  const submit = async () => {
    setErr('');
    if (!shiftId) { setErr('Pick a shift'); return; }
    const payload = ctx.meds.map(m => ({ inventory_id: m.inventory_id,
      shelf_count: (lines[m.inventory_id] || {}).shelf,
      note: (lines[m.inventory_id] || {}).note || '' }));
    if (payload.some(p => p.shelf_count === undefined || p.shelf_count === ''))
      { setErr('Every line needs a shelf count'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/emr/facilities/${facilityId}/controlled-counts`,
        { shift_id: shiftId, witness_id: witness || null, lines: payload });
      setResult(r.data); setOpen(false); setLines({}); setShiftId(''); load();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Submit failed'); }
    setBusy(false);
  };
  const loadHist = async () => {
    if (hist) { setHist(null); return; }
    try { setHist((await axios.get(`${API}/api/emr/facilities/${facilityId}/controlled-counts?days=7`)).data); }
    catch { setHist([]); }
  };
  return (
    <div style={{ marginTop: 14, border: `1px solid ${T.b200}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <span style={{ fontWeight:600, fontSize:14 }}>Controlled Count</span>
        <span style={{ background:'#ede7f6', color:'#4527a0', borderRadius:6, padding:'1px 8px',
          fontSize:11, fontWeight:600 }}>{ctx.meds.length} controlled</span>
        <button style={{ ...btn(false), padding:'4px 12px', fontSize:12 }}
          onClick={() => { setOpen(!open); setResult(null); }}>{open ? 'Cancel' : 'Shift Count'}</button>
        <button style={{ ...btn(false), padding:'4px 12px', fontSize:12 }}
          onClick={loadHist}>{hist ? 'Hide history' : 'History'}</button>
        {ctx.today && ctx.today.length > 0 && !open &&
          <span style={{ fontSize:12, color:T.inkSoft }}>Today: {ctx.today.length} count{ctx.today.length > 1 ? 's' : ''}</span>}
      </div>
      {result && <div style={{ marginTop:10, padding:'8px 12px', borderRadius:8, fontSize:13, fontWeight:600,
        background: result.status === 'clean' ? '#e8f5e9' : '#fff3e0',
        color: result.status === 'clean' ? '#1b5e20' : '#e65100' }}>
        {result.status === 'clean' ? '\u2713 Clean count recorded'
          : '\u26a0 ' + result.discrepancies + ' discrepancy(ies) recorded \u2014 use Count correction on the affected med to adjust the book'}</div>}
      {err && <div style={{ marginTop:8, color:'#b30000', fontSize:13, fontWeight:600 }}>{err}</div>}
      {open && (
        <div style={{ marginTop:12, display:'grid', gap:10 }}>
          <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <select value={shiftId} onChange={e => setShiftId(e.target.value)}
              style={{ padding:'6px 10px', borderRadius:8, border:`1px solid ${T.b200}`, fontSize:13 }}>
              <option value="">Shift…</option>
              {ctx.shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {ctx.signatures_required === 2 &&
              <input placeholder="Witness user ID (2nd signature required)" value={witness}
                onChange={e => setWitness(e.target.value)}
                style={{ padding:'6px 10px', borderRadius:8, border:`1px solid ${T.b200}`, fontSize:13, width:280 }} />}
          </div>
          {ctx.meds.map(m => {
            const ln = lines[m.inventory_id] || {};
            const shelf = ln.shelf;
            const disc = shelf !== undefined && shelf !== '' && Number(shelf) !== Number(m.book_count);
            return (
              <div key={m.inventory_id} style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap',
                padding:'8px 10px', borderRadius:8, background: disc ? '#fdecea' : '#fafafa' }}>
                <span style={{ fontWeight:600, fontSize:13, minWidth:160 }}>{m.patient_name}</span>
                <span style={{ fontSize:13 }}>{m.medication_name}</span>
                <span style={{ background:'#ede7f6', color:'#4527a0', borderRadius:6, padding:'1px 8px',
                  fontSize:11, fontWeight:600 }}>{m.controlled_schedule}</span>
                {m.rx_number && <span style={{ fontFamily:'monospace', fontSize:11, color:T.inkSoft }}>Rx {m.rx_number}</span>}
                <span style={{ fontSize:13, color:T.inkSoft }}>Book: <b>{m.book_count}</b>{m.unit ? ' ' + m.unit : ''}</span>
                <input type="number" min="0" placeholder="Shelf" value={shelf === undefined ? '' : shelf}
                  onChange={e => setLine(m.inventory_id, { shelf: e.target.value })}
                  style={{ width:80, padding:'5px 8px', borderRadius:8, border:`1px solid ${disc ? '#b30000' : T.b200}`, fontSize:13 }} />
                {disc && <input placeholder="Discrepancy note (required)" value={ln.note || ''}
                  onChange={e => setLine(m.inventory_id, { note: e.target.value })}
                  style={{ flex:1, minWidth:200, padding:'5px 8px', borderRadius:8, border:'1px solid #b30000', fontSize:13 }} />}
              </div>);
          })}
          <button disabled={busy} onClick={submit}
            style={{ ...btn(true), padding:'8px 16px', fontSize:13, justifySelf:'start' }}>
            {busy ? 'Recording\u2026' : 'Sign & record count'}</button>
        </div>)}
      {hist && (
        <div style={{ marginTop:12, display:'grid', gap:8 }}>
          {!hist.length && <div style={{ fontSize:13, color:T.inkSoft }}>No counts in the last 7 days.</div>}
          {hist.map(s => (
            <div key={s.id} style={{ padding:'8px 10px', borderRadius:8, background:'#fafafa', fontSize:13 }}>
              <b>{s.count_date && s.count_date.slice(0,10)}</b> · {s.shift_name} · {s.counted_by_name}
              {s.witness_name ? ' + ' + s.witness_name : ''} ·{' '}
              <span style={{ fontWeight:600, color: s.status === 'clean' ? '#1b5e20' : '#e65100' }}>{s.status}</span>
              {(s.lines || []).filter(l => l.is_discrepancy).map((l, i) => (
                <div key={i} style={{ color:'#b30000', marginTop:2 }}>
                  {l.patient_name} · {l.medication_name}: book {l.book_count} vs shelf {l.shelf_count} — {l.note}</div>))}
            </div>))}
        </div>)}
    </div>
  );
}

function MedInventory({ patientId, episodeId, active }) {
  const [rows, setRows] = useState([]);
  const [ierr, setIerr] = useState('');
  const [open, setOpen] = useState(null);
  const [txn, setTxn] = useState(null); // {invId, type, quantity, actual_count, reason, witness_name}
  const [orders, setOrders] = useState([]);
  const [track, setTrack] = useState(null);
  const [rxEdit, setRxEdit] = useState(null);
  const [rxVal, setRxVal] = useState('');
  const saveRx = async (r) => {
    try {
      await axios.patch(`${API}/api/emr/med-orders/${r.order_id}/set-rx`, { rx_number: rxVal });
      setRxEdit(null); load();
    } catch (e) { alert('Could not save Rx #'); }
  }; // {order_id, quantity}
  const load = async () => {
    try {
      const params = episodeId ? { scope_episode: episodeId } : {};
      const [ivr, orr] = await Promise.all([
        axios.get(`${API}/api/emr/patients/${patientId}/med-inventory`, { params }),
        axios.get(`${API}/api/emr/patients/${patientId}/med-orders`, { params })]);
      setRows(ivr.data || []); setOrders(orr.data || []); setIerr('');
    } catch { setIerr('Could not load inventory'); }
  };
  useEffect(() => { if (patientId && active) load(); }, [patientId, active]);
  const untracked = orders.filter(o => o.status === 'active' && !rows.some(r => r.order_id === o.id));
  const startTrack = async () => {
    try {
      await axios.post(`${API}/api/emr/patients/${patientId}/med-inventory/start`,
        { order_id: track.order_id, quantity: track.quantity, rx_number: track.rx_number || null });
      setTrack(null); load();
    } catch (e) { setIerr((e.response && e.response.data && e.response.data.error) || 'Could not start tracking'); }
  };
  const submitTxn = async () => {
    try {
      await axios.post(`${API}/api/emr/med-inventory/${txn.invId}/txn`, {
        txn_type: txn.type, quantity: txn.quantity || null,
        actual_count: txn.actual_count === '' ? null : txn.actual_count,
        reason: txn.reason || null, witness_name: txn.witness_name || null,
        rx_number: txn.rx_number || null });
      setTxn(null); load();
    } catch (e) { setIerr((e.response && e.response.data && e.response.data.error) || 'Transaction failed'); }
  };
  const tLabel = { seed_brought_in:'Seeded (brought in)', refill:'Refill', mar_dose:'Dose given',
    adjustment:'Count correction', destruction:'Destroyed', discharge_return:'Returned at discharge' };
  if (!rows.length && !untracked.length && !ierr) return null;
  return (
    <div style={{ ...card, marginTop:16 }}>
      <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}`,
        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
        <span><strong>Med Inventory / Counts</strong>
        <span style={{ fontSize:11.5, color:T.inkSoft, marginLeft:10 }}>Centrally stored & self-administered medication counts</span></span>
        {untracked.length > 0 && !track &&
          <button style={btn(false)} onClick={() => setTrack({ order_id:'', quantity:'', rx_number:'' })}>+ Track count</button>}
      </div>
      <div style={{ padding:18 }}>
        {ierr && <div style={{ color:T.blocking, fontSize:13, marginBottom:8 }}>{ierr}</div>}
        {track && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:10,
            padding:'9px 11px', background:'#fafafa', borderRadius:8 }}>
            <select style={{ ...input, width:260 }} value={track.order_id}
              onChange={e => {
                const o = untracked.find(u => u.id === e.target.value);
                setTrack({ ...track, order_id:e.target.value,
                  rx_number: (o && o.rx_number) || track.rx_number || '' });
              }}>
              <option value="">Choose medication…</option>
              {untracked.map(o => <option key={o.id} value={o.id}>{o.medication_name}</option>)}
            </select>
            <input style={{ ...input, width:130 }} placeholder="Starting count *"
              value={track.quantity} onChange={e => setTrack({ ...track, quantity:e.target.value })} />
            <input style={{ ...input, width:130 }} placeholder="Rx #"
              value={track.rx_number} onChange={e => setTrack({ ...track, rx_number:e.target.value })} />
            <button style={btn(true)} onClick={startTrack}
              disabled={!track.order_id || track.quantity === '' || isNaN(Number(track.quantity))}>Start tracking</button>
            <button style={btn(false)} onClick={() => setTrack(null)}>Cancel</button>
          </div>
        )}
        {rows.map(r => (
          <div key={r.id} style={{ padding:'10px 0', borderBottom:`1px solid ${T.b100}` }}>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <strong>{r.medication_name}</strong>
              {r.controlled_schedule && <span style={{ background:'#ede7f6', color:'#4527a0', borderRadius:6,
                padding:'1px 8px', fontSize:11, fontWeight:600 }}>{r.controlled_schedule}</span>}
              {r.order_source === 'brought_in' && <span style={{ background:'#fff3e0', color:'#e65100',
                border:'1px solid #ffcc80', borderRadius:6, padding:'1px 8px', fontSize:11, fontWeight:600 }}>BROUGHT IN</span>}
              {rxEdit === r.id ? (
                <span style={{ display:'inline-flex', gap:4, alignItems:'center' }}>
                  <input autoFocus style={{ ...input, width:110, padding:'2px 6px', fontSize:12 }} placeholder="Rx #"
                    value={rxVal} onChange={e => setRxVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveRx(r); if (e.key === 'Escape') setRxEdit(null); }} />
                  <button style={{ ...btn(true), padding:'2px 8px', fontSize:11 }} onClick={() => saveRx(r)}>Save</button>
                  <button style={{ ...btn(false), padding:'2px 8px', fontSize:11 }} onClick={() => setRxEdit(null)}>✕</button>
                </span>
              ) : r.rx_number ? (
                <span title="Click to edit Rx #" onClick={() => { setRxEdit(r.id); setRxVal(r.rx_number); }}
                  style={{ fontFamily:'monospace', fontSize:11.5, color:T.inkSoft, cursor:'pointer',
                  background:'#f5f5f5', borderRadius:6, padding:'1px 8px' }}>Rx {r.rx_number}</span>
              ) : (
                <span onClick={() => { setRxEdit(r.id); setRxVal(''); }}
                  style={{ fontSize:11, color:T.accent, cursor:'pointer', textDecoration:'underline' }}>Set Rx#</span>
              )}
              <span style={{ fontWeight:600, fontSize:14,
                color: r.quantity_on_hand == null ? '#e65100' : (Number(r.quantity_on_hand) <= 7 ? '#e65100' : T.ink) }}>
                {r.quantity_on_hand == null ? 'COUNT NEEDED' : `${r.quantity_on_hand} ${r.unit || ''} on hand`}</span>
              <span style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                <button style={btn(false)} onClick={() => setTxn({ invId:r.id, type:'refill', quantity:'', reason:'', witness_name:'', actual_count:'', rx_number:'' })}>+ Refill</button>
                <button style={btn(false)} onClick={() => setTxn({ invId:r.id, type:'adjustment', quantity:'', reason:'', witness_name:'', actual_count:'' })}>Count correction</button>
                <button style={btn(false)} onClick={() => setTxn({ invId:r.id, type:'destruction', quantity:'', reason:'', witness_name:'', actual_count:'' })}>Destroy</button>
                <button style={btn(false)} onClick={() => setOpen(open === r.id ? null : r.id)}>
                  {open === r.id ? 'Hide log' : 'Log'}</button>
              </span>
            </div>
            {txn && txn.invId === r.id && (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginTop:8,
                padding:'9px 11px', background:'#fafafa', borderRadius:8 }}>
                <strong style={{ fontSize:12.5 }}>{tLabel[txn.type]}:</strong>
                {txn.type === 'adjustment' ? (
                  <>
                    <span style={{ fontSize:12.5, color:T.inkSoft }}>Book: {r.quantity_on_hand == null ? '—' : r.quantity_on_hand}</span>
                    <input style={{ ...input, width:110 }} placeholder="Actual count *"
                      value={txn.actual_count} onChange={e => setTxn({ ...txn, actual_count:e.target.value })} />
                    <input style={{ ...input, flex:1, minWidth:220 }} placeholder="Why is the count different? (required)"
                      value={txn.reason} onChange={e => setTxn({ ...txn, reason:e.target.value })} />
                  </>
                ) : (
                  <>
                    <input style={{ ...input, width:100 }} placeholder="Quantity *"
                      value={txn.quantity} onChange={e => setTxn({ ...txn, quantity:e.target.value })} />
                    {txn.type === 'destruction' && (
                      <>
                        <input style={{ ...input, width:200 }} placeholder="Reason (required)"
                          value={txn.reason} onChange={e => setTxn({ ...txn, reason:e.target.value })} />
                        <input style={{ ...input, width:200 }} placeholder="Witness staff name (required)"
                          value={txn.witness_name} onChange={e => setTxn({ ...txn, witness_name:e.target.value })} />
                      </>
                    )}
                    {txn.type === 'refill' && (
                      <>
                        <input style={{ ...input, width:130 }} placeholder="Rx #"
                          value={txn.rx_number || ''} onChange={e => setTxn({ ...txn, rx_number:e.target.value })} />
                        <input style={{ ...input, width:200 }} placeholder="Note (optional)"
                          value={txn.reason} onChange={e => setTxn({ ...txn, reason:e.target.value })} />
                      </>
                    )}
                  </>
                )}
                <button style={btn(true)} onClick={submitTxn}
                  disabled={txn.type === 'adjustment' ? (txn.actual_count === '' || !txn.reason.trim())
                    : txn.type === 'destruction' ? (!txn.quantity || !txn.reason.trim() || !txn.witness_name.trim())
                    : !txn.quantity}>Save</button>
                <button style={btn(false)} onClick={() => setTxn(null)}>Cancel</button>
              </div>
            )}
            {open === r.id && (
              <div style={{ marginTop:8, fontSize:12.5 }}>
                {(r.txns || []).map(t => (
                  <div key={t.id} style={{ display:'flex', gap:10, padding:'4px 0', borderBottom:`1px dotted ${T.b100}`, flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'monospace', color:T.inkSoft, whiteSpace:'nowrap' }}>
                      {new Date(t.created_at).toLocaleString('en-US', { timeZone:'America/Los_Angeles', month:'numeric', day:'numeric', hour:'numeric', minute:'2-digit' })}</span>
                    <span style={{ fontWeight:600 }}>{tLabel[t.txn_type] || t.txn_type}</span>
                    <span style={{ color: Number(t.delta) < 0 ? '#b30000' : T.pass, fontWeight:600 }}>
                      {Number(t.delta) > 0 ? '+' : ''}{t.delta}</span>
                    <span>→ {t.balance_after == null ? '—' : t.balance_after}</span>
                    {t.txn_type === 'adjustment' && <span style={{ color:T.inkSoft }}>
                      (book {t.expected_count} → actual {t.actual_count})</span>}
                    {t.reason && <span style={{ color:T.inkSoft }}>{t.reason}</span>}
                    {t.rx_number && <span style={{ fontFamily:'monospace', color:T.inkSoft }}>Rx {t.rx_number}</span>}
                    {t.witness_name && <span style={{ color:T.inkSoft }}>witness: {t.witness_name}</span>}
                    <span style={{ color:T.inkSoft, marginLeft:'auto' }}>{t.actor}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Frequency → facility pass keys (mirrors lib/medPassRoutes DEFAULT_FREQ_MAP; the facility's own map overrides)
const FREQ_OPTIONS = ['QD','QAM','QHS','BID','TID','QID'];
const FREQ_MAP_DEFAULT = { QD:['am'], DAILY:['am'], QAM:['am'], QPM:['pm'], QHS:['hs','pm'], BID:['am','pm'], TID:['am','mid','pm'], QID:['am','mid','pm','hs'] };
function passTimesFor(cfg, freq) {
  if (!cfg || !cfg.passes || !freq) return null;
  const code = String(freq).toUpperCase().replace(/[.\s-]/g, '');
  const map = { ...FREQ_MAP_DEFAULT, ...(cfg.frequency_map || {}) };
  const keys = map[code]; if (!keys) return null;
  if (code === 'QHS' || code === 'QPM') { const p = keys.map(k => cfg.passes.find(x => x.key === k)).find(Boolean); return p ? [p.at] : null; }
  const out = keys.map(k => { const p = cfg.passes.find(x => x.key === k); return p ? p.at : null; });
  return out.every(Boolean) ? out : null;
}
const TIME_OK = /^([01]?\d|2[0-3]):[0-5]\d$/;
const badTimes = (str) => String(str || '').split(',').map(t => t.trim()).filter(Boolean).filter(t => !TIME_OK.test(t));

function MedOrders({ patientId, episodeId, active, facilityId }) {
  const [passCfg, setPassCfg] = useState(null);
  useEffect(() => {
    if (!facilityId) { setPassCfg(null); return; }
    axios.get(`${API}/api/emr/med-pass/${facilityId}`).then(r => setPassCfg(r.data)).catch(() => setPassCfg(null));
  }, [facilityId]);
  const [orders, setOrders] = useState([]);
  const [merr, setMerr] = useState('');
  const [showDc, setShowDc] = useState(false);
  const [q, setQ] = useState('');
  const [nf, setNf] = useState(null);
  const [dcFor, setDcFor] = useState(null);
  const [dcReason, setDcReason] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [protoSel, setProtoSel] = useState('');
  const [protoStart, setProtoStart] = useState('');
  const [protoOv, setProtoOv] = useState({});
  const ovSet = (seq, k, v) => setProtoOv(p => ({ ...p, [seq]: { ...(p[seq] || {}), [k]: v } }));
  const [medSugs, setMedSugs] = useState([]);
  const [strengths, setStrengths] = useState([]);
  useEffect(() => { (async () => {
    try { setProtocols((await axios.get(`${API}/api/emr/med-protocols`)).data || []); } catch {}
  })(); }, []);
  const searchMeds = async (text) => {
    if ((text || '').trim().length < 2) { setMedSugs([]); return; }
    try { setMedSugs((await axios.get(`${API}/api/emr/med-search`, { params:{ q:text } })).data || []); } catch {}
  };
  const pickMed = async (m) => {
    setNf(f => ({ ...f, medication_name: m.display_name, route: m.route || f.route }));
    setMedSugs([]);
    try { setStrengths((await axios.get(`${API}/api/emr/med-strengths`, { params:{ display_name: m.display_name } })).data || []); } catch {}
  };
  const applyProtocol = async () => {
    try {
      const items = ((protocols.find(p => p.id === protoSel) || {}).items || []);
      const item_overrides = items.map(i => ({ seq: i.seq, ...(protoOv[i.seq] || {}) }))
        .filter(o => o.exclude || o.strength || o.duration_days);
      // SCHEDULE CONFIRMATION (ticket 71d168d7): show every medication and its day-by-day steps before anything starts.
      const proto = protocols.find(p => p.id === protoSel) || {};
      const startTxt = (protoStart ? new Date(protoStart) : new Date()).toLocaleString([], { month:'numeric', day:'numeric', hour:'numeric', minute:'2-digit' });
      const stepTxt = (sg) => (sg.day_from ? 'Day ' + sg.day_from + (sg.day_to && (sg.day_to === sg.day_from) === false ? '-' + sg.day_to : '') + ': ' : '')
        + [sg.amount, unitTag(sg.unit), sg.strength, sg.frequency].filter(Boolean).join(' ') + (sg.is_prn ? ' as needed' : '')
        + ((sg.schedule_times || []).length ? ' at ' + sg.schedule_times.map(t => (t && t.at) || t).join(', ') : '');
      const lines = items.filter(i => ((protoOv[i.seq] || {}).exclude ? false : true)).map(i => {
        const ov = protoOv[i.seq] || {};
        const segsI = (i.segments || []).map(sg => ov.strength ? { ...sg, strength: ov.strength } : sg);
        const lastDay = segsI.reduce((m, sg) => Math.max(m, Number(sg.day_to || sg.day_from || 0)), 0);
        const days = ov.duration_days ? Number(ov.duration_days) : (i.duration_days || null);
        const note = (ov.duration_days && lastDay && (Number(ov.duration_days) === lastDay) === false) ? (days > lastDay ? `\n   NOTE: you entered ${days} days, but the dose steps only cover days 1 to ${lastDay}. No doses will appear on the med pass after day ${lastDay}.` : `\n   NOTE: you entered ${days} days, but the dose steps run to day ${lastDay}. The order will end at about day ${days}, before the later steps are reached.`) : '';
        return `${i.medication_name} (${i.order_type}${days ? ', ' + days + ' days' : ''})\n` + segsI.map(sg => '   ' + stepTxt(sg)).join('\n') + note;
      });
      if (window.confirm(`${proto.name || 'Protocol'}\nStarts ${startTxt}\n\n${lines.join('\n\n')}\n\nStart these orders?`) === false) return;
      const pbody = { protocol_id: protoSel, episode_id: episodeId || null, item_overrides,
        start_at: protoStart ? new Date(protoStart).toISOString() : new Date().toISOString() };
      let r;
      try { r = await axios.post(`${API}/api/emr/patients/${patientId}/apply-protocol`, pbody); }
      catch (de) {
        const dd = de.response && de.response.data;
        if (dd && dd.needs_duplicate_confirm) {
          if (window.confirm(dd.error) === false) { setMerr('Protocol not applied. The client already has one of those medications active.'); return; }
          r = await axios.post(`${API}/api/emr/patients/${patientId}/apply-protocol`, { ...pbody, confirm_duplicate: true });
        } else throw de;
      }
      setProtoSel(''); setProtoStart(''); load();
      setMerr('');
      window.alert(`${r.data.protocol}: ${r.data.orders.length} orders created (${r.data.status})`);
    } catch (e) { setMerr((e.response && e.response.data && e.response.data.error) || 'Protocol apply failed'); }
  };
  const load = async () => {
    try { setOrders((await axios.get(`${API}/api/emr/patients/${patientId}/med-orders`, { params: episodeId ? { scope_episode: episodeId } : {} })).data || []); setMerr(''); }
    catch { setMerr('Could not load medication orders'); }
  };
  useEffect(() => { if (patientId && active) load(); }, [patientId, active]);

  const newForm = () => { setEditingId(null); setNf({
    medication_name:'', generic_name:'', route:'oral', dosage_form:'tablet',
    order_type:'scheduled', ordered_via:'direct', justification:'', warnings_text:'',
    order_source:'facility', rx_number:'', rx_expiration:'', original_prescriber:'',
    last_dose_note:'', amount_brought_in:'',
    hold_sbp_min:'', hold_dbp_min:'', hold_pulse_min:'', hold_if_sedated:false,
    controlled_schedule:'', duration_text:'', start_at:'', end_at:'',
    segments:[EMPTY_SEG()]
  }); };

  const toLocal = (iso) => { if (!iso) return ''; const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + 'T' + p(d.getHours()) + ':' + p(d.getMinutes()); };
  const editOrder = (o) => {
    setEditingId(o.id);
    setNf({
      medication_name: o.medication_name || '', generic_name: o.generic_name || '', route: o.route || 'oral',
      dosage_form: o.dosage_form || '', order_type: o.order_type || 'scheduled', ordered_via: o.ordered_via || 'direct',
      justification: o.justification || '', warnings_text: o.warnings_text || '',
      hold_sbp_min: o.hold_sbp_min || '', hold_dbp_min: o.hold_dbp_min || '', hold_pulse_min: o.hold_pulse_min || '',
      hold_if_sedated: !!o.hold_if_sedated, controlled_schedule: o.controlled_schedule || '',
      duration_text: o.duration_text || '', start_at: toLocal(o.start_at), end_at: toLocal(o.end_at),
      order_source: o.order_source || 'facility', rx_number: o.rx_number || '',
      rx_expiration: o.rx_expiration ? String(o.rx_expiration).slice(0, 10) : '',
      original_prescriber: o.original_prescriber || '', last_dose_note: o.last_dose_note || '',
      amount_brought_in: o.amount_brought_in || '',
      segments: (o.segments || []).map(s => ({ day_from: s.day_from || '', day_to: s.day_to || '',
        frequency: s.frequency || '', is_prn: !!s.is_prn, amount: s.amount || '', strength: s.strength || '',
        unit: s.unit || '', times: (s.schedule_times || []).map(t => t.at).join(', '), times_source: s.times_source || 'prescriber' }))
    });
    toOrderForm();
  };
  // bring the order form into view: it sits above the list, so Edit pressed far down the page used to look like nothing happened
  const toOrderForm = () => setTimeout(() => { const el = document.getElementById('tcio-med-order-form'); if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 80);
  const endedAt = (o) => o.end_at ? new Date(o.end_at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit' }) : '';
  const datesWrong = (f) => !!(f && f.start_at && f.end_at && new Date(f.end_at) < new Date(f.start_at));
  // RENEW: an ended order cannot be edited. This opens a NEW order filled in from it, starting now; the ended order stays as it was.
  const renewOrder = (o) => {
    setEditingId(null);
    setNf({
      _renewOf: o.medication_name || 'this medication',
      medication_name: o.medication_name || '', generic_name: o.generic_name || '', route: o.route || 'oral',
      dosage_form: o.dosage_form || '', order_type: o.order_type || 'scheduled', ordered_via: 'direct',
      justification: o.justification || '', warnings_text: o.warnings_text || '',
      hold_sbp_min: o.hold_sbp_min || '', hold_dbp_min: o.hold_dbp_min || '', hold_pulse_min: o.hold_pulse_min || '',
      hold_if_sedated: !!o.hold_if_sedated, controlled_schedule: o.controlled_schedule || '',
      duration_text: o.duration_text || '', start_at: toLocal(new Date().toISOString()), end_at: '',
      order_source: o.order_source || 'facility', rx_number: o.rx_number || '',
      rx_expiration: o.rx_expiration ? String(o.rx_expiration).slice(0, 10) : '',
      original_prescriber: o.original_prescriber || '', last_dose_note: o.last_dose_note || '',
      amount_brought_in: o.amount_brought_in || '',
      segments: (o.segments || []).length ? (o.segments || []).map(s => ({ day_from: s.day_from || '', day_to: s.day_to || '',
        frequency: s.frequency || '', is_prn: !!s.is_prn, amount: s.amount || '', strength: s.strength || '',
        unit: s.unit || '', times: (s.schedule_times || []).map(t => t.at).join(', '), times_source: s.times_source || 'prescriber' })) : [EMPTY_SEG()]
    });
    toOrderForm();
  };
  const submitOrder = async () => {
    try {
      if (datesWrong(nf)) { setMerr('The end is before the start. An order cannot end before it starts. Change the end or the start, then save again.'); return; }
      const segs = nf.segments.map(s => ({
        day_from: s.day_from ? Number(s.day_from) : null,
        day_to: s.day_to ? Number(s.day_to) : null,
        frequency: s.frequency, is_prn: !!s.is_prn,
        amount: s.amount || null, strength: s.strength || null, unit: s.unit || null,
        schedule_times: (s.times || '').split(',').map(t => t.trim()).filter(Boolean),
        times_source: s.times_source || 'prescriber'
      }));
      const body = { ...nf, episode_id: episodeId || null,
        hold_sbp_min: nf.hold_sbp_min ? Number(nf.hold_sbp_min) : null,
        hold_dbp_min: nf.hold_dbp_min ? Number(nf.hold_dbp_min) : null,
        hold_pulse_min: nf.hold_pulse_min ? Number(nf.hold_pulse_min) : null,
        controlled_schedule: nf.controlled_schedule || null,
        start_at: nf.start_at ? new Date(nf.start_at).toISOString() : null,
        end_at: nf.end_at ? new Date(nf.end_at).toISOString() : null,
        segments: nf.order_type === 'supply' ? [] : segs };
      if (editingId) {
        const r = await axios.put(`${API}/api/emr/med-orders/${editingId}`, body);
        if (r.data && r.data.status === 'pending_review')
          window.alert('Edit saved — order returned to PENDING REVIEW (prescriber signature required to reactivate)');
      } else {
        try { await axios.post(`${API}/api/emr/patients/${patientId}/med-orders`, body); }
        catch (de) {
          // DUPLICATE QUESTION (ticket 71d168d7): the server asks before adding a medication the client already has active.
          const dd = de.response && de.response.data;
          if (dd && dd.needs_duplicate_confirm) {
            if (window.confirm(dd.error) === false) { setMerr('Not added. The client already has that medication active.'); return; }
            await axios.post(`${API}/api/emr/patients/${patientId}/med-orders`, { ...body, confirm_duplicate: true });
          } else throw de;
        }
      }
      setEditingId(null); setNf(null); load();
    } catch (e) { setMerr((e.response && e.response.data && e.response.data.error) || 'The order was not saved and the system gave no reason. Check your connection and try again. If it keeps happening, press Report Issue.'); }
  };

  const review = async (id) => {
    try { await axios.post(`${API}/api/emr/med-orders/${id}/review`); load(); }
    catch (e) {
      const d = (e.response && e.response.data) || {};
      if (d.code === 'dea_required') {
        const dea = window.prompt('This is a controlled substance order. Enter your DEA number to review it (2 letters + 7 digits):');
        if (dea && dea.trim()) {
          try {
            await axios.post(`${API}/api/emr/my-dea`, { dea_number: dea.trim() });
            await axios.post(`${API}/api/emr/med-orders/${id}/review`);
            load(); return;
          } catch (e2) { setMerr((e2.response && e2.response.data && e2.response.data.error) || 'DEA save failed'); return; }
        }
        setMerr('Review cancelled — a DEA number on file is required for controlled substance orders.'); return;
      }
      setMerr(d.error || 'The order was not activated and the system gave no reason. Check your connection and try again. If it keeps happening, press Report Issue.');
    }
  };
  const discontinue = async () => {
    try {
      await axios.post(`${API}/api/emr/med-orders/${dcFor}/discontinue`, { reason: dcReason });
      setDcFor(null); setDcReason(''); load();
    } catch (e) { setMerr((e.response && e.response.data && e.response.data.error) || 'The order was not discontinued and the system gave no reason. Check your connection and try again. If it keeps happening, press Report Issue.'); }
  };

  // Orders list line reads quantity, unit, strength — "1 TAB 5 mg Q6H PRN", never "1 5 mg" (Karlee / Chad 11/2). Unit falls back to the order's dosage form for imported orders.
  const seg = (s, o) => (s.day_from ? `Day ${s.day_from}${s.day_to && s.day_to !== s.day_from ? '-' + s.day_to : ''}: ` : '') +
    [s.amount, unitTag(s.unit || (o && o.dosage_form)), s.strength, s.frequency].filter(Boolean).join(' ') + (s.is_prn ? ' PRN' : '') +
    ((s.schedule_times || []).length ? ' @ ' + s.schedule_times.map(t => t.at).join(', ') : '');

  const chip = (o) => {
    const map = { active:[T.passBg, T.pass], pending_review:['#fff3e0','#e65100'],
      discontinued:[T.blockingBg, T.blocking], completed:['#eceff1','#546e7a'], draft:['#f2f2f2','#616161'] };
    const [b, c] = map[eff(o)] || map.draft;
    return <span style={{ background:b, color:c, borderRadius:99, padding:'1px 10px', fontSize:11, fontWeight:600 }}>
      {eff(o) === 'completed' ? 'ENDED ' + new Date(o.end_at).toLocaleDateString('en-US', { month:'2-digit', day:'2-digit' }) : eff(o).replace('_',' ').toUpperCase()}</span>;
  };

  const holds = (o) => {
    const parts = [];
    if (o.hold_sbp_min) parts.push(`SBP<${o.hold_sbp_min}`);
    if (o.hold_dbp_min) parts.push(`DBP<${o.hold_dbp_min}`);
    if (o.hold_pulse_min) parts.push(`P<${o.hold_pulse_min}`);
    if (o.hold_if_sedated) parts.push('sedation');
    return parts.length ? <span style={{ background:'#fff8e1', color:'#8d6e00', border:'1px solid #e6c200',
      borderRadius:6, padding:'1px 8px', fontSize:11.5, fontWeight:600 }}>HOLD: {parts.join(' · ')}</span> : null;
  };

  const eff = (o) => (o.status === 'active' && o.end_at && new Date(o.end_at) < new Date()) ? 'completed' : o.status;
  const matches = (o) => !q.trim() || (o.medication_name + ' ' + (o.generic_name || '') + ' ' + (o.justification || ''))
    .toLowerCase().includes(q.trim().toLowerCase());
  const liveStatuses = ['active','pending_review','draft'];
  const recentlyEnded = (o) => eff(o) === 'completed' && (Date.now() - new Date(o.end_at).getTime()) < 7 * 86400000;
  const visible = orders.filter(o => matches(o) && (showDc || liveStatuses.includes(eff(o)) || recentlyEnded(o)));
  const dcCount = orders.length - orders.filter(o => liveStatuses.includes(eff(o)) || recentlyEnded(o)).length;

  return (
    <div style={{ ...card, marginBottom:16 }}>
      <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}`,
        display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <strong>Doctor's Orders</strong>
        <span style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
          <select style={{ ...input, width:200 }} value={protoSel}
            onChange={e => { setProtoSel(e.target.value); setProtoOv({}); }}>
            <option value="">Apply protocol…</option>
            {protocols.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input style={{ ...input, width:190 }} placeholder="Search orders…"
            value={q} onChange={e => setQ(e.target.value)} />
          {dcCount > 0 && <button style={btn(false)} onClick={() => setShowDc(!showDc)}>
            {showDc ? 'Hide' : 'Show'} inactive ({dcCount})</button>}
          <button style={btn(false)} onClick={async () => {
            try {
              const tk = localStorage.getItem('tcio_token') || '';
              const qs = 'sections=dr_orders' + (episodeId ? '&episode_id=' + episodeId : '');
              const r = await fetch(`${API}/api/emr/patients/${patientId}/casefile.pdf?` + qs, { headers:{ Authorization:'Bearer '+tk } });
              if (!r.ok) { alert(r.status === 403 ? 'Your role cannot export records. Ask your program manager.' : 'Could not generate the PDF (' + r.status + ')'); return; }
              const blob = await r.blob();
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = 'doctors-orders.pdf';
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(a.href), 4000);
            } catch (err) { alert('Download failed: ' + err.message); }
          }}>Download PDF</button>
          <button style={btn(true)} onClick={() => { if (nf) { setNf(null); setEditingId(null); } else newForm(); }}>{nf ? 'Cancel' : '+ New order'}</button>
        </span>
      </div>
      <div style={{ padding:18 }}>
        {merr && <div style={{ color:T.blocking, fontSize:13, marginBottom:8 }}>{merr}</div>}
        {protoSel && (
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap',
            padding:'10px 12px', background:'#f5f9f5', borderRadius:8, marginBottom:10 }}>
            <strong style={{ fontSize:12.5 }}>
              {(protocols.find(p => p.id === protoSel) || {}).name} — review & adjust:</strong>
            <div style={{ width:'100%', display:'grid', gap:5 }}>
              {((protocols.find(p => p.id === protoSel) || {}).items || []).map(i => {
                const ov = protoOv[i.seq] || {};
                const inc = !ov.exclude;
                return (
                  <div key={i.seq} style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap',
                    opacity: inc ? 1 : 0.45, fontSize:12.5 }}>
                    <label style={{ display:'flex', gap:5, alignItems:'center', minWidth:210 }}>
                      <input type="checkbox" checked={inc} onChange={e => ovSet(i.seq, 'exclude', !e.target.checked)} />
                      <b>{i.medication_name}</b>
                    </label>
                    <input style={{ ...input, width:90 }} placeholder={((i.segments || [])[0] || {}).strength || 'strength'}
                      value={ov.strength || ''} onChange={e => ovSet(i.seq, 'strength', e.target.value)} disabled={!inc} />
                    <input style={{ ...input, width:80 }} placeholder={i.duration_days ? i.duration_days + ' days' : 'days'}
                      value={ov.duration_days || ''} onChange={e => ovSet(i.seq, 'duration_days', e.target.value)} disabled={!inc} />
                    <span style={{ color:T.inkSoft }}>{i.order_type}{i.controlled_schedule ? ' · ' + i.controlled_schedule : ''}</span>
                  </div>
                );
              })}
            </div>
            <span style={{ marginLeft:'auto', display:'flex', gap:6, alignItems:'center' }}>
              <label style={{ fontSize:12 }}>Start <input type="datetime-local" style={input}
                value={protoStart} onChange={e => setProtoStart(e.target.value)} /></label>
              <button style={btn(true)} onClick={applyProtocol}>Sign & apply set</button>
              <button style={btn(false)} onClick={() => setProtoSel('')}>Cancel</button>
            </span>
          </div>
        )}
        {!visible.length && !nf && <div style={{ color:T.inkSoft }}>No medication orders.</div>}
        {visible.map(o => (
          <div key={o.id} style={{ padding:'11px 0', borderBottom:`1px solid ${T.b100}` }}>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <strong>{o.medication_name}</strong>
              {o.generic_name && <span style={{ color:T.inkSoft, fontSize:12.5 }}>({o.generic_name})</span>}
              {o.controlled_schedule && <span style={{ background:'#ede7f6', color:'#4527a0', borderRadius:6,
                padding:'1px 8px', fontSize:11, fontWeight:600 }}>{o.controlled_schedule}</span>}
              {o.order_source === 'brought_in' && <span style={{ background:'#fff3e0', color:'#e65100',
                border:'1px solid #ffcc80', borderRadius:6, padding:'1px 8px', fontSize:11, fontWeight:600 }}
                title={['Rx# ' + (o.rx_number || '—'), 'Orig prescriber: ' + (o.original_prescriber || '—'),
                  'Exp: ' + (o.rx_expiration ? String(o.rx_expiration).slice(0,10) : '—'),
                  'Last dose: ' + (o.last_dose_note || '—'), 'Amount: ' + (o.amount_brought_in || '—')].join(' | ')}>
                BROUGHT IN</span>}
              {chip(o)}
              {holds(o)}
              <span style={{ marginLeft:'auto', fontSize:12, color:T.inkSoft }}>
                {o.dosage_form ? `${o.dosage_form} · ` : ''}{o.route} · {o.order_type}{o.duration_text ? ` · ${o.duration_text}` : ''}</span>
            </div>
            <div style={{ fontSize:12.5, color:T.ink, marginTop:5 }}>
              {(o.segments || []).map((s, i) => <div key={i}>{seg(s, o)}</div>)}
            </div>
            {o.justification && <div style={{ fontSize:12, color:T.inkSoft, marginTop:3 }}>For: {o.justification}</div>}
            <div style={{ fontSize:11.5, color:T.inkSoft, marginTop:3 }}>
              Ordered by {o.ordered_by_name}{o.ordered_via !== 'direct' ? ` via ${o.ordered_via}, entered by ${o.entered_by_name}` : ''}
              {o.reviewed_by_name ? ` · reviewed by ${o.reviewed_by_name}` : ''}
              {o.status === 'discontinued' && ` · DC'd by ${o.discontinued_by_name}: ${o.discontinue_reason}`}
            </div>
            {eff(o) === 'completed' && (
              <div style={{ display:'flex', gap:10, marginTop:7, alignItems:'center', flexWrap:'wrap' }}>
                <span style={{ fontSize:12, color:T.inkSoft }}>This order ended {endedAt(o)}, so it can no longer be edited.{o.start_at && new Date(o.end_at) < new Date(o.start_at) ? ' Its end date was entered before its start date.' : ''} To continue this medication, renew it as a new order.</span>
                <button style={btn(true)} onClick={() => renewOrder(o)}>Renew</button>
              </div>
            )}
            {['active','pending_review'].includes(eff(o)) && (
              <div style={{ display:'flex', gap:8, marginTop:7 }}>
                {o.status === 'pending_review' && <button style={btn(true)} onClick={() => review(o.id)}>{o.authorizing_attachment_id ? 'Verify against signed order & activate' : 'Review & activate'}</button>}
                <button style={btn(false)} onClick={() => editOrder(o)}>{o.order_type === 'taper' ? 'Adjust taper' : 'Edit'}</button>
                {dcFor === o.id ? (
                  <>
                    <input style={{ ...input, width:260 }} placeholder="Discontinue reason (required)"
                      value={dcReason} onChange={e => setDcReason(e.target.value)} />
                    <button style={btn(true)} onClick={discontinue} disabled={!dcReason.trim()}>Confirm DC</button>
                    <button style={btn(false)} onClick={() => { setDcFor(null); setDcReason(''); }}>Cancel</button>
                  </>
                ) : <button style={btn(false)} onClick={() => setDcFor(o.id)}>Discontinue</button>}
              </div>
            )}
          </div>
        ))}
        {nf && (
          <div style={{ marginTop:14, display:'grid', gap:9 }}>
            <div style={{ display:'flex', gap:14, alignItems:'center', fontSize:12.5 }}>
              <label style={{ display:'flex', gap:5, alignItems:'center', cursor:'pointer' }}>
                <input type="radio" checked={nf.order_source !== 'brought_in'}
                  onChange={() => setNf({ ...nf, order_source:'facility' })} /> Prescribed here</label>
              <label style={{ display:'flex', gap:5, alignItems:'center', cursor:'pointer' }}>
                <input type="radio" checked={nf.order_source === 'brought_in'}
                  onChange={() => setNf({ ...nf, order_source:'brought_in' })} /> Brought In</label>
            </div>
            {nf.order_source === 'brought_in' && (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', padding:'9px 11px',
                background:'#fff8f0', border:'1px solid #ffcc80', borderRadius:8 }}>
                <input style={{ ...input, width:120 }} placeholder="Rx #"
                  value={nf.rx_number} onChange={e => setNf({ ...nf, rx_number:e.target.value })} />
                <label style={{ fontSize:12 }}>Expires <input type="date" style={input}
                  value={nf.rx_expiration} onChange={e => setNf({ ...nf, rx_expiration:e.target.value })} /></label>
                <input style={{ ...input, width:170 }} placeholder="Original prescriber"
                  value={nf.original_prescriber} onChange={e => setNf({ ...nf, original_prescriber:e.target.value })} />
                <input style={{ ...input, width:170 }} placeholder="Last dose taken (when)"
                  value={nf.last_dose_note} onChange={e => setNf({ ...nf, last_dose_note:e.target.value })} />
                <input style={{ ...input, width:150 }} placeholder="Amount brought in"
                  value={nf.amount_brought_in} onChange={e => setNf({ ...nf, amount_brought_in:e.target.value })} />
              </div>
            )}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <span style={{ position:'relative' }}>
                <input style={{ ...input, width:220 }} placeholder="Medication * (type to search)" value={nf.medication_name}
                  onChange={e => { setNf({ ...nf, medication_name:e.target.value }); searchMeds(e.target.value); }} />
                {medSugs.length > 0 && (
                  <div style={{ position:'absolute', top:'100%', left:0, zIndex:30, background:'#fff',
                    border:`1px solid ${T.b100}`, borderRadius:8, boxShadow:'0 4px 14px rgba(0,0,0,0.12)',
                    width:300, maxHeight:220, overflowY:'auto' }}>
                    {medSugs.map((m, i) => (
                      <div key={i} onClick={() => pickMed(m)}
                        style={{ padding:'7px 11px', cursor:'pointer', fontSize:12.5, borderBottom:`1px solid ${T.b100}` }}>
                        {m.display_name}</div>
                    ))}
                  </div>
                )}
              </span>
              {strengths.length > 0 && (
                <select style={{ ...input, width:150 }} defaultValue=""
                  onChange={e => {
                    const s = strengths[Number(e.target.value)];
                    if (!s) return;
                    setNf(f => ({ ...f, dosage_form: s.dose_form || f.dosage_form,
                      segments: f.segments.map((sg, i) => i === 0 ? { ...sg, strength: s.strength } : sg) }));
                  }}>
                  <option value="">Strength…</option>
                  {strengths.map((s, i) => <option key={i} value={i}>{s.strength} {s.dose_form}</option>)}
                </select>
              )}
              <input style={{ ...input, width:150 }} placeholder="Generic" value={nf.generic_name}
                onChange={e => setNf({ ...nf, generic_name:e.target.value })} />
              <input style={{ ...input, width:90 }} placeholder="Route *" value={nf.route}
                onChange={e => setNf({ ...nf, route:e.target.value })} />
              <input style={{ ...input, width:120 }} placeholder="Form *" list="tcio-dosage-forms" required value={nf.dosage_form}
                onChange={e => setNf({ ...nf, dosage_form:e.target.value })} /> {/* 10/24 Cyndie 13b40b18 */}
              <datalist id="tcio-dosage-forms">
                {['tablet','capsule','mL (liquid)','solution','suspension','injection','patch','inhaler','nasal spray','drops','cream','ointment','gel','suppository','sublingual','film','powder','lozenge','unit'].map(f => <option key={f} value={f} />)}
              </datalist>
              <select style={{ ...input, width:120 }} value={nf.order_type}
                onChange={e => setNf(e.target.value === 'supply' ? { ...nf, order_type:'supply', route:'supply', controlled_schedule:'' } : { ...nf, order_type:e.target.value })}>
                {['scheduled','prn','one_time','taper','supply'].map(t => <option key={t} value={t}>{t === 'supply' ? 'supply (lancets, needles, strips)' : t}</option>)}
              </select>
              <select style={{ ...input, width:150 }} value={nf.controlled_schedule}
                onChange={e => setNf({ ...nf, controlled_schedule:e.target.value })}>
                <option value="">Not controlled</option>
                {['C-II','C-III','C-IV','C-V'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select style={{ ...input, width:120 }} value={nf.ordered_via}
                onChange={e => setNf({ ...nf, ordered_via:e.target.value })}>
                {['direct','verbal','paper','telephone'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              <span id="tcio-med-order-form" style={{ flexBasis:'100%', height:0 }} />
              {nf._renewOf && <div style={{ flexBasis:'100%', background:'#e8f1fb', border:'1px solid #b6d4f2', borderRadius:6, padding:'7px 10px', fontSize:12.5 }}>
                <b>Renewing {nf._renewOf}.</b> This saves as a new order. The ended order stays in the chart as it was. Check the dose lines, set the start, and set an end if there is one.</div>}
              <label style={{ fontSize:12.5 }}>Start * <input type="datetime-local" style={input} value={nf.start_at}
                onChange={e => setNf({ ...nf, start_at:e.target.value })} /></label>
              <label style={{ fontSize:12.5 }}>End <input type="datetime-local" style={input} value={nf.end_at}
                onChange={e => setNf({ ...nf, end_at:e.target.value })} /></label>
              {datesWrong(nf) && <div style={{ flexBasis:'100%', color:T.blocking, fontSize:12.5, fontWeight:600 }}>The end is before the start. An order cannot end before it starts. Change one of them to save this order.</div>}
              <input style={{ ...input, width:130 }} placeholder="Duration (e.g. 6 day taper)" value={nf.duration_text}
                onChange={e => setNf({ ...nf, duration_text:e.target.value })} />
            </div>
            <input style={input} placeholder="Justification" value={nf.justification}
              onChange={e => setNf({ ...nf, justification:e.target.value })} />
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:12.5, fontWeight:600 }}>Holds:</span>
              <input style={{ ...input, width:90 }} placeholder="SBP <" value={nf.hold_sbp_min}
                onChange={e => setNf({ ...nf, hold_sbp_min:e.target.value })} />
              <input style={{ ...input, width:90 }} placeholder="DBP <" value={nf.hold_dbp_min}
                onChange={e => setNf({ ...nf, hold_dbp_min:e.target.value })} />
              <input style={{ ...input, width:90 }} placeholder="Pulse <" value={nf.hold_pulse_min}
                onChange={e => setNf({ ...nf, hold_pulse_min:e.target.value })} />
              <label style={{ fontSize:12.5 }}><input type="checkbox" checked={nf.hold_if_sedated}
                onChange={e => setNf({ ...nf, hold_if_sedated:e.target.checked })} /> Hold if sedated</label>
              <input style={{ ...input, flex:1, minWidth:200 }} placeholder="Warnings (free text)" value={nf.warnings_text}
                onChange={e => setNf({ ...nf, warnings_text:e.target.value })} />
            </div>
            <div style={{ fontSize:12.5, fontWeight:600 }}>{nf.order_type === 'supply' ? 'Supply: no schedule needed. It is recorded and inventoried, and never appears on the med pass.' : 'Schedule segments (tapers = multiple):'}</div>
            {(nf.order_type === 'supply' ? [] : nf.segments).map((s, i) => (
              <div key={i} style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center',
                padding:8, background:'#fafafa', borderRadius:8 }}>
                <input style={{ ...input, width:70 }} placeholder="Day from" value={s.day_from}
                  onChange={e => { const g=[...nf.segments]; g[i]={...s, day_from:e.target.value}; setNf({ ...nf, segments:g }); }} />
                <input style={{ ...input, width:70 }} placeholder="Day to" value={s.day_to}
                  onChange={e => { const g=[...nf.segments]; g[i]={...s, day_to:e.target.value}; setNf({ ...nf, segments:g }); }} />
                {(s.freq_other || (s.frequency && !FREQ_OPTIONS.includes(String(s.frequency).toUpperCase()))) ? (
                  <input style={{ ...input, width:140 }} placeholder="Frequency * (q6h…)" value={s.frequency} autoFocus={!!s.freq_other}
                    onChange={e => { const g=[...nf.segments]; g[i]={...s, frequency:e.target.value}; setNf({ ...nf, segments:g }); }}
                    onBlur={() => { if (!s.frequency) { const g=[...nf.segments]; g[i]={...s, freq_other:false}; setNf({ ...nf, segments:g }); } }} />
                ) : (
                  <select style={{ ...input, width:140 }} value={s.frequency ? String(s.frequency).toUpperCase() : ''}
                    onChange={e => {
                      const v = e.target.value; const g=[...nf.segments];
                      if (v === '__other') { g[i]={...s, frequency:'', freq_other:true, times:'', times_source:'prescriber' }; }
                      else {
                        const pre = passTimesFor(passCfg, v);
                        const keep = s.times_source === 'override' && (s.times || '').trim();
                        g[i]={...s, frequency:v, freq_other:false, times: keep ? s.times : (pre ? pre.join(', ') : s.times), times_source: keep ? 'override' : (pre ? 'facility_pass' : 'prescriber') };
                      }
                      setNf({ ...nf, segments:g });
                    }}>
                    <option value="">Frequency *</option>
                    {FREQ_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                    <option value="__other">Other…</option>
                  </select>
                )}
                <input style={{ ...input, width:60 }} placeholder="Amt" value={s.amount}
                  onChange={e => { const g=[...nf.segments]; g[i]={...s, amount:e.target.value}; setNf({ ...nf, segments:g }); }} />
                <input style={{ ...input, width:80 }} placeholder="Strength" value={s.strength}
                  onChange={e => { const g=[...nf.segments]; g[i]={...s, strength:e.target.value}; setNf({ ...nf, segments:g }); }} />
                <input style={{ ...input, width:80 }} placeholder="Unit" value={s.unit}
                  onChange={e => { const g=[...nf.segments]; g[i]={...s, unit:e.target.value}; setNf({ ...nf, segments:g }); }} />
                <span style={{ display:'inline-flex', flexDirection:'column' }}>
                  <input style={{ ...input, width:150, borderColor: badTimes(s.times).length ? '#dc2626' : undefined }} placeholder="Times (09:00, 21:00)" value={s.times}
                    title={s.times_source === 'facility_pass' ? 'From facility med passes' : s.times_source === 'override' ? 'Prescriber-set times (never realigned)' : 'HH:MM, comma-separated'}
                    onChange={e => { const g=[...nf.segments]; g[i]={...s, times:e.target.value, times_source: e.target.value.trim() ? 'override' : 'prescriber' }; setNf({ ...nf, segments:g }); }} />
                  {s.times_source === 'facility_pass' && <span style={{ fontSize:10, color:'#047857' }}>facility passes</span>}
                  {s.times_source === 'override' && <span style={{ fontSize:10, color:'#b45309' }}>prescriber times</span>}
                  {badTimes(s.times).length > 0 && <span style={{ fontSize:10, color:'#dc2626' }}>use HH:MM — {badTimes(s.times).join(', ')}</span>}
                </span>
                <label style={{ fontSize:12 }}><input type="checkbox" checked={s.is_prn}
                  onChange={e => { const g=[...nf.segments]; g[i]={...s, is_prn:e.target.checked}; setNf({ ...nf, segments:g }); }} /> PRN</label>
                {nf.segments.length > 1 && <button style={btn(false)}
                  onClick={() => setNf({ ...nf, segments: nf.segments.filter((_, j) => j !== i) })}>×</button>}
              </div>
            ))}
            <div style={{ display:'flex', gap:8 }}>
              <button style={btn(false)} onClick={() => setNf({ ...nf, segments:[...nf.segments, EMPTY_SEG()] })}>+ Segment</button>
              <button style={btn(true)} onClick={submitOrder}
                disabled={!nf.medication_name || !nf.route || !nf.start_at || (nf.order_type === 'supply' ? false : (nf.segments.some(s => !s.frequency) || nf.segments.some(s => !s.is_prn && !(s.times || '').trim())))}>
                {editingId ? 'Save changes' : 'Create order'}</button>
              {(() => {
                const missing = [];
                if (!nf.medication_name) missing.push('Medication');
                if (!nf.route) missing.push('Route');
                if (!nf.start_at) missing.push('Start date/time');
                if (nf.segments.some(s => !s.frequency)) missing.push('Frequency (each segment)');
                if (nf.segments.some(s => !s.is_prn && !(s.times || '').trim())) missing.push('Times (non-PRN segments \u2014 MAR rows come from them)');
                if (nf.segments.some(s => badTimes(s.times).length)) missing.push('Times must be HH:MM (e.g. 09:00, 21:00) \u2014 ' + nf.segments.flatMap(s => badTimes(s.times)).join(', '));
                return missing.length ? (
                  <span style={{ fontSize:12, color:'#e65100', alignSelf:'center', fontWeight:600 }}>
                    Missing: {missing.join(' \u00b7 ')}</span>
                ) : null;
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



// ── Chart Attachments (external documents: hospital paperwork, outside records, labs) ──
// Section map for the chart document chain (Trina build A).
// 'other' also catches any category no section claims, so nothing ever hides.
const ATTACH_SECTIONS = [
  { key: 'client_id', title: 'Client ID', cats: ['identification'], hint: 'Photo ID' },
  { key: 'insurance', title: 'Insurance Card', cats: ['insurance'], hint: 'Front and back', sides: true },
  { key: 'ua', title: 'UA / Drug Screens', cats: ['ua'], hint: 'Result photo' },
  { key: 'belongings', title: 'Personal Belongings', cats: ['belongings'], hint: 'Inventory + photos' },
  { key: 'destruction', title: 'Med Destruction / AOR / CIR', cats: ['med_destruction', 'aor', 'cir'], pick: true },
  { key: 'ext_clinical', title: 'External Clinical Documents', cats: ['history_physical', 'signed_med_order', 'psych_evaluation', 'followup_encounter'], pick: true, performed: true, hint: 'Signed H&P / med orders / psych evals done outside TCIO - compliance clock reads the performed date' },
  { key: 'outside', title: 'Outside Medical Records', cats: ['outside_records', 'hospital', 'legal', 'screening_tool', 'lab_result'], pick: true, hint: 'Hospital / ED, legal, screenings' },
  { key: 'other', title: 'Other', cats: ['other'] },
];
const ATTACH_CATS = { outside_records: 'Outside Records', hospital: 'Hospital', lab_result: 'Lab Result',
  insurance: 'Insurance', identification: 'ID', legal: 'Legal', screening_tool: 'Screening Tool', other: 'Other',
  ua: 'UA', belongings: 'Belongings', med_destruction: 'Med Destruction', aor: 'AOR', cir: 'CIR',
  history_physical: 'H&P (External Practitioner)', signed_med_order: 'Signed Med Order (External)', psych_evaluation: 'Psychiatric Evaluation (External)', followup_encounter: 'Follow Up Encounter (External)' };

// ICD-10 typeahead (Catherine 8119bb21). Module-level per component-identity landmine.
// Type a code prefix (F33) or description words (major dep); picking fills code + description.
// Free-typing stays allowed for codes outside the curated library.
// Diagnoses the reader found in uploaded documents (2026-10-03). Each shows the document and the line it was read from.
// Nothing here is on the chart or billable until someone confirms it against the document. Module-level per component-identity landmine.
function DxFindings({ patientId, onChanged }) {
  const [rows, setRows] = React.useState([]);
  const [busy, setBusy] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [mayConfirm, setMayConfirm] = React.useState(true);
  const load = React.useCallback(async () => {
    try { const r = await axios.get(`${API}/api/emr/patients/${patientId}/dx-findings`); setRows(r.data.findings || []); setMayConfirm(r.data.may_confirm !== false); } catch { setRows([]); }
  }, [patientId]);
  React.useEffect(() => { load(); }, [load]);
  const decide = async (f, what) => {
    setBusy(f.id); setMsg('');
    try { await axios.post(`${API}/api/emr/dx-findings/${f.id}/${what}`); await load(); if (what === 'confirm' && onChanged) onChanged(); }
    catch (e) { setMsg((e.response && e.response.data && e.response.data.error) || 'Could not save'); }
    setBusy(null);
  };
  const open = async (f) => {
    try { const r = await axios.get(`${API}/api/emr/attachments/${f.attachment_id}/download`); if (r.data && r.data.url) window.open(r.data.url, '_blank'); } catch { setMsg('Could not open the document'); }
  };
  if (!rows.length) return null;
  const fd = (v) => { const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[3]}/${m[1]}` : ''; };
  return (
    <div style={{ background:'#FFFBEB', border:'1px solid #FCD34D', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
      <div style={{ fontSize:13, fontWeight:700, color:'#92400E' }}>Read from uploaded documents - not yet confirmed ({rows.length})</div>
      <div style={{ fontSize:12, color:'#92400E', marginTop:2, marginBottom:8 }}>Check each against the document, then confirm. A diagnosis is not on the chart and cannot be billed until it is confirmed. Claims carry the substance diagnosis first, then the other substance and mental health diagnoses.{mayConfirm ? '' : ' Clinical staff, a program manager, a case manager or an administrator confirms these.'}</div>
      {msg ? <div style={{ fontSize:12.5, color:'#991B1B', marginBottom:6 }}>{msg}</div> : null}
      {rows.map(f => (
        <div key={f.id} style={{ padding:'8px 0', borderTop:'1px solid #FDE68A' }}>
          <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ flex:1, minWidth:200 }}>
              <span style={{ fontFamily:'monospace', fontWeight:700 }}>{f.icd10_code}</span>{' '}{f.description}
              {f.is_primary ? <span style={{ marginLeft:8, fontSize:11, fontWeight:700, color:'#92400E' }}>principal for billing</span> : null}
              {!/^F/.test(f.icd10_code) ? <span style={{ marginLeft:8, fontSize:11, color:T.inkSoft }}>stays on the chart, not on claims</span> : null}
              {!f.in_code_list ? <span style={{ marginLeft:8, fontSize:11, color:T.inkSoft }}>not in TCIO's code list</span> : null}
            </div>
            <button style={{ ...btn(true), opacity: busy === f.id ? 0.55 : 1 }} disabled={busy === f.id} hidden={!mayConfirm} onClick={() => decide(f, 'confirm')}>Confirm</button>
            <button style={{ ...btn(false), opacity: busy === f.id ? 0.55 : 1 }} disabled={busy === f.id} hidden={!mayConfirm} onClick={() => decide(f, 'reject')}>Not right</button>
          </div>
          <div style={{ fontSize:12, color:T.inkSoft, marginTop:3 }}>
            {(f.documents && f.documents.length ? f.documents : [f]).map((d, i) => (
              <div key={i}>From the {d.source} dated {fd(d.doc_date)}{' '}
                <span style={{ color:T.ink, textDecoration:'underline', cursor:'pointer' }} onClick={() => open(d)}>{d.filename}</span>
                {d.quote ? <span>{' '}- "{d.quote}"</span> : null}</div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DxTypeahead({ dxForm, setDxForm }) {
  const [opts, setOpts] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const timer = React.useRef(null);
  const search = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) { setOpts([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/emr/icd10-search`, { params: { q } });
        setOpts(r.data.results || []); setOpen((r.data.results || []).length > 0);
      } catch { setOpts([]); setOpen(false); }
    }, 200);
  };
  const pick = (o) => {
    setDxForm(f => ({ ...f, icd10_code: o.code, description: o.description }));
    setOpen(false); setOpts([]);
  };
  return (
    <div style={{ position:'relative' }}>
      <input style={{ ...input, width:'100%', boxSizing:'border-box' }} placeholder="Type code or diagnosis (e.g. F33 or major dep)"
        value={dxForm.icd10_code}
        onChange={e => { setDxForm({ ...dxForm, icd10_code: e.target.value }); search(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 180)} />
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:30, background:'#fff',
          border:`1px solid ${T.b200}`, borderRadius:8, boxShadow:'0 6px 20px rgba(0,0,0,.12)', maxHeight:280, overflowY:'auto' }}>
          {opts.map(o => (
            <div key={o.code} onMouseDown={() => pick(o)}
              style={{ padding:'8px 12px', cursor:'pointer', borderBottom:`1px solid ${T.b100}`, fontSize:13 }}>
              <span style={{ fontFamily:'monospace', fontWeight:700 }}>{o.code}</span>
              {' '}{o.description}
              {!o.primary_dx_eligible && <span style={{ marginLeft:8, color:T.inkSoft, fontSize:11 }}>usually secondary</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentsPanel({ patientId, sections, compact }) {
  const [items, setItems] = React.useState([]);
  const [expanded, setExpanded] = React.useState({});
  const [openSec, setOpenSec] = React.useState(null);
  const [file, setFile] = React.useState(null);
  const [cat, setCat] = React.useState('');
  const [side, setSide] = React.useState('Front');
  const [docDate, setDocDate] = React.useState('');
  const [performedBy, setPerformedBy] = React.useState('');
  const [performedAt, setPerformedAt] = React.useState('');
  const [desc, setDesc] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const secs = ATTACH_SECTIONS.filter(sd => !sections || sections.includes(sd.key));
  const load = React.useCallback(async () => {
    try { setItems((await axios.get(`${API}/api/emr/patients/${patientId}/attachments`)).data); }
    catch (e) { setMsg('Load failed: ' + (e.response?.data?.error || e.message)); }
  }, [patientId]);
  React.useEffect(() => { load(); }, [load]);
  const claimed = React.useMemo(() => new Set(ATTACH_SECTIONS.filter(sd => sd.key !== 'other').flatMap(sd => sd.cats)), []);
  const secItems = (sd) => items.filter(a => sd.key === 'other' ? (a.category === 'other' || !claimed.has(a.category)) : sd.cats.includes(a.category));
  const openUpload = (sd) => { setOpenSec(sd.key); setCat(sd.cats[0]); setSide('Front'); setFile(null); setDesc(''); setDocDate(''); setPerformedBy(''); setPerformedAt(''); setMsg(''); };
  const doUpload = async (sd) => {
    if (!file) { setMsg('Choose a file first.'); return; }
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', cat || sd.cats[0]);
      if (docDate) fd.append('doc_date', docDate);
      if (sd.performed && performedBy) fd.append('performed_by', performedBy);
      if (sd.performed && performedAt) fd.append('performed_at', performedAt);
      const d = sd.sides ? (side + (desc ? ' - ' + desc : '')) : desc;
      if (d) fd.append('description', d);
      await axios.post(`${API}/api/emr/patients/${patientId}/attachments`, fd);
      setOpenSec(null); setFile(null); setDesc(''); setDocDate('');
      load();
    } catch (e) { setMsg('Upload failed: ' + (e.response?.data?.error || e.message)); }
    setBusy(false);
  };
  const doDownload = async (id) => {
    const w = window.open('', '_blank');
    try {
      const r = (await axios.get(`${API}/api/emr/attachments/${id}/download`)).data;
      if (w) { w.location = r.url; }
      else {
        const a = document.createElement('a');
        a.href = r.url; a.target = '_blank'; a.rel = 'noopener';
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch (e) {
      if (w) w.close();
      setMsg('Download failed: ' + (e.response?.data?.error || e.message));
    }
  };
  const doVoid = async (id) => {
    const reason = window.prompt('Void reason (required):');
    if (!reason) return;
    try { await axios.post(`${API}/api/emr/attachments/${id}/void`, { reason }); load(); }
    catch (e) { setMsg('Void failed: ' + (e.response?.data?.error || e.message)); }
  };
  return (
    <div style={compact ? { marginBottom: 12 } : { ...card, padding: 16, marginBottom: 16 }}>
      {!compact && <><strong>Documents</strong>
      <span style={{ color: T.inkSoft, marginLeft: 10, fontSize: 12.5 }}>Chart document chain - every view is audited</span></>}
      {msg && <div style={{ color: T.blocking, fontSize: 12.5, marginTop: 6 }}>{msg}</div>}
      {secs.map(sd => {
        const list = secItems(sd);
        const isOpen = !compact || expanded[sd.key] || openSec === sd.key;
        return (
          <div key={sd.key} style={{ marginTop: compact ? 6 : 14, border: `1px solid ${T.b100}`, borderRadius: compact ? 8 : 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: compact ? '6px 10px' : '9px 12px', background: T.b100,
                cursor: compact ? 'pointer' : 'default' }}
              onClick={compact ? () => setExpanded(x => ({ ...x, [sd.key]: !x[sd.key] })) : undefined}>
              <strong style={{ fontSize: compact ? 12.5 : 13.5 }}>{sd.title}</strong>
              {!compact && sd.hint && <span style={{ color: T.inkSoft, fontSize: 11.5 }}>{sd.hint}</span>}
              <span style={{ marginLeft: 'auto', color: T.inkSoft, fontSize: 11.5 }}>
                {list.length ? `${list.length} file${list.length > 1 ? 's' : ''}` : (compact ? 'empty' : '')}</span>
              <button style={{ ...btn(false), padding: '4px 12px', fontSize: 12 }}
                onClick={e => { e.stopPropagation(); openUpload(sd); if (compact) setExpanded(x => ({ ...x, [sd.key]: true })); }}>+ Add</button>
            </div>
            {isOpen && <>
            {openSec === sd.key && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', padding: 12, borderBottom: `1px solid ${T.b100}` }}>
                <div><div style={label}>File</div>
                  <input type="file" accept="image/*,.pdf" onChange={e => setFile(e.target.files[0] || null)} style={{ fontSize: 12.5, maxWidth: 220 }} /></div>
                {sd.sides && <div><div style={label}>Side</div>
                  <select value={side} onChange={e => setSide(e.target.value)} style={{ ...input, width: 90 }}>
                    <option>Front</option><option>Back</option>
                  </select></div>}
                {sd.pick && <div><div style={label}>Type</div>
                  <select value={cat} onChange={e => setCat(e.target.value)} style={{ ...input, width: 160 }}>
                    {sd.cats.map(c => <option key={c} value={c}>{ATTACH_CATS[c] || c}</option>)}
                  </select></div>}
                <div><div style={label}>Document Date</div>
                  <input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} style={{ ...input, width: 140 }} /></div>
                {sd.performed && <div><div style={label}>Performed by (practitioner)</div>
                  <input value={performedBy} onChange={e => setPerformedBy(e.target.value)}
                    placeholder="e.g. Christopher Dono, PA-C" style={{ ...input, width: 200 }} /></div>}
                {sd.performed && <div><div style={label}>Performed date &amp; time</div>
                  <input type="datetime-local" value={performedAt} onChange={e => setPerformedAt(e.target.value)}
                    style={{ ...input, width: 190 }} /></div>}
                <div style={{ flex: 1, minWidth: 150 }}><div style={label}>Description</div>
                  <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional" style={input} /></div>
                <button style={btn(true)} disabled={busy} onClick={() => doUpload(sd)}>{busy ? 'Uploading...' : 'Upload'}</button>
                <button style={btn(false)} disabled={busy} onClick={() => setOpenSec(null)}>Cancel</button>
              </div>
            )}
            {list.length === 0 && openSec !== sd.key &&
              <div style={{ color: T.inkSoft, fontSize: 12.5, padding: '10px 12px' }}>Nothing uploaded yet.</div>}
            {list.map(a => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderTop: `1px solid ${T.b100}`, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, color: T.ink, fontSize: 13.5 }}>{a.filename}</span>
                  <span style={{ background: T.infoBg, color: T.info, borderRadius: 99, padding: '1px 9px',
                    fontSize: 11, fontWeight: 600, marginLeft: 8 }}>{ATTACH_CATS[a.category] || a.category}</span>
                  <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 2 }}>
                    {a.doc_date ? `Doc date ${String(a.doc_date).slice(0, 10)} - ` : ''}
                    Uploaded by {a.uploaded_by_name} on {String(a.created_at).slice(0, 10)}
                    {a.description ? ` - ${a.description}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...btn(false), padding: '5px 12px', fontSize: 12 }} onClick={() => doDownload(a.id)}>Open</button>
                  <button style={{ ...btn(false), padding: '5px 12px', fontSize: 12 }} onClick={() => doVoid(a.id)}>Void</button>
                </div>
              </div>
            ))}
            </>}
          </div>
        );
      })}
    </div>
  );
}

function RoundsHistory({ episodeId, onStartRound }) {
  const [obs, setObs] = useState([]);
  const [rerr, setRerr] = useState('');
  const [dueInfo, setDueInfo] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { (async () => {
    if (!episodeId) return;
    try { setObs(((await axios.get(`${API}/api/rounds/${episodeId}/observations`)).data.data) || []); setRerr(''); }
    catch { setRerr('Could not load rounds'); }
    try {
      const eq = await axios.get(`${API}/api/rounds/board-episode?episodeId=${episodeId}`);
      setDueInfo(eq.data || null);
    } catch { /* endpoint may not exist yet; chip just hides */ }
  })(); }, [episodeId, reload]);
  const rdDue = dueInfo && (dueInfo.overdue_minutes != null
    ? { txt: 'ROUND OVERDUE +' + dueInfo.overdue_minutes + 'm', c: '#dc2626', bg: '#fef2f2' }
    : dueInfo.due_now ? { txt: 'ROUND DUE NOW', c: '#dc2626', bg: '#fef2f2' }
    : dueInfo.next_due ? { txt: 'Next round: ' + new Date(dueInfo.next_due).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }), c: T.pass, bg: T.passBg } : null);
  return (
    <div style={card}>
      <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}`,
        display:'flex', justifyContent:'space-between', alignItems:'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <strong>Observation Rounds</strong>
          {rdDue && <span style={{ background: rdDue.bg, color: rdDue.c, borderRadius: 99, padding: '2px 10px', fontSize: 11.5, fontWeight: 800 }}>{rdDue.txt}</span>}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize:11.5, color:T.inkSoft }}>{obs.length} logged</span>
          {onStartRound && <button onClick={() => onStartRound(episodeId)}
            style={{ background: T.rose, color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 14px', fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>Start Round</button>}
        </div>
      </div>
      <RoundLevelControl episodeId={episodeId} user={window.__tcio_user || {}} />
      <div style={{ padding:18 }}>
        {rerr && <div style={{ color:T.blocking, fontSize:13 }}>{rerr}</div>}
        {!obs.length && !rerr && <div style={{ color:T.inkSoft }}>No rounds logged for this episode yet.</div>}
        {obs.map(o => (
          <div key={o.id} style={{ padding:'9px 0', borderBottom:`1px solid ${T.b100}`,
            display:'flex', gap:12, alignItems:'baseline', flexWrap:'wrap' }}>
            <span style={{ fontFamily:'monospace', fontSize:12.5, color:T.inkSoft, whiteSpace:'nowrap' }}>
              {new Date(o.observed_at).toLocaleString('en-US', { month:'numeric', day:'numeric', hour:'numeric', minute:'2-digit' })}</span>
            <span style={{ fontWeight:600 }}>{o.activity}</span>
            <span style={{ color:T.inkSoft }}>{o.location}</span>
            {o.alert && <span style={{ background:T.blockingBg, color:T.blocking, borderRadius:99,
              padding:'1px 9px', fontSize:11, fontWeight:600 }}>ALERT</span>}
            <span style={{ marginLeft:'auto', fontSize:12, color:T.inkSoft }}>{o.observed_by_name || ''}</span>
            {o.notes && <div style={{ width:'100%', fontSize:12.5, color:T.ink }}>{o.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Chart Prescreen tab: resolves the patient's prescreen via the lead spine and
// renders the live PDF in a framed viewer. Blob-fetched with auth (iframes can't
// send headers). Draft prescreens get an honest banner, absent ones an empty state.
function PrescreenTab({ patientId, active }) {
  const [meta, setMeta] = React.useState(null);   // {none, reason} | {prescreen}
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [err, setPErr] = React.useState('');
  React.useEffect(() => {
    if (!active || !patientId) return;
    let dead = false; let objUrl = null;
    setMeta(null); setPdfUrl(null); setPErr('');
    (async () => {
      try {
        const m = await axios.get(`${API}/api/emr/patients/${patientId}/prescreen`);
        if (dead) return;
        setMeta(m.data);
        if (m.data && m.data.ok && !m.data.none) {
          const tok = localStorage.getItem('tcio_token');
          const r = await fetch(`${API}/api/prescreens/${m.data.prescreen.id}/pdf`,
            { headers: { Authorization: 'Bearer ' + tok } });
          if (!r.ok) throw new Error('PDF fetch failed (' + r.status + ')');
          const blob = await r.blob();
          objUrl = URL.createObjectURL(blob);
          if (!dead) setPdfUrl(objUrl);
        }
      } catch (e) { if (!dead) setPErr(e.message); }
    })();
    return () => { dead = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [patientId, active]);
  const dt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '';
  if (!active) return null;
  if (err) return <div style={{ color:T.blocking, padding:20, fontSize:13.5 }}>Could not load the prescreen: {err}</div>;
  if (!meta) return <div style={{ color:T.inkSoft, padding:20, fontSize:13.5 }}>Loading prescreen…</div>;
  if (meta.none) return (
    <div style={{ background:T.surface, border:'1px solid '+T.b200, borderRadius:10, padding:'28px 24px', textAlign:'center' }}>
      <div style={{ fontSize:15, fontWeight:600, color:T.ink }}>No prescreen on file</div>
      <div style={{ fontSize:13, color:T.inkSoft, marginTop:4 }}>
        {meta.reason === 'no_lead'
          ? 'This chart is not linked to an admissions lead, so no prescreen can be located.'
          : 'No prescreen was created for this patient during admissions.'}
      </div>
    </div>
  );
  const p = meta.prescreen;
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', background:T.surface,
        border:'1px solid '+T.b200, borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
        <span style={{ fontSize:14, fontWeight:700, color:T.ink }}>Admissions Prescreen</span>
        {p.status === 'draft' && <span style={{ background:T.warningBg, color:T.warning, borderRadius:6, fontSize:11, fontWeight:700, padding:'2px 8px' }}>Draft — not yet completed</span>}
        {p.status === 'pending_dr_approval' && <span style={{ background:T.infoBg, color:T.info, borderRadius:6, fontSize:11, fontWeight:700, padding:'2px 8px' }}>Pending Dr Approval</span>}
        <span style={{ fontSize:12.5, color:T.inkSoft }}>
          Updated {dt(p.updated_at || p.created_at)}{p.completed_by ? ' by ' + p.completed_by : ''}
        </span>
        <button style={{ marginLeft:'auto', background:'#fff', color:T.b700, border:'1.5px solid '+T.b200,
          borderRadius:8, padding:'6px 12px', fontWeight:600, fontSize:12, cursor:'pointer' }}
          onClick={async () => {
            try {
              const tk = localStorage.getItem('tcio_token') || '';
              const r = await fetch(`${API}/api/prescreens/${p.id}/pdf`, { headers:{ Authorization:'Bearer '+tk } });
              if (!r.ok) { alert('Could not generate the PDF (' + r.status + ')'); return; }
              const blob = await r.blob();
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = 'prescreen.pdf';
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(a.href), 4000);
            } catch (e) { alert('Download failed: ' + e.message); }
          }}>Download PDF</button>
      </div>
      {pdfUrl
        ? <iframe title="prescreen-pdf" src={pdfUrl}
            style={{ width:'100%', height:'75vh', border:'1px solid '+T.b200, borderRadius:10, background:'#fff' }} />
        : <div style={{ color:T.inkSoft, padding:20, fontSize:13.5 }}>Rendering PDF…</div>}
    </div>
  );
}

// Demographics edit modal (ratified 9/3): program_manager/admin/ceo only; server enforces.
function DemographicsEditModal({ patient, onClose, onSaved }) {
  const d0 = patient.dob ? String(patient.dob).slice(0, 10) : '';
  const [f, setF] = React.useState({
    first_name: patient.first_name || '', last_name: patient.last_name || '',
    dob: d0, gender: patient.gender || '', phone: patient.phone || '', email: patient.email || '',
    address: patient.address || '', city: patient.city || '', state: patient.state || '', zip: patient.zip || '',
    pronouns: patient.pronouns || '', preferred_language: patient.preferred_language || '',
    marital_status: patient.marital_status || '', plan_type: patient.plan_type || '',
    insurance_effective_date: patient.insurance_effective_date ? String(patient.insurance_effective_date).slice(0, 10) : '',
    insurance_termination_date: patient.insurance_termination_date ? String(patient.insurance_termination_date).slice(0, 10) : '',
    insurance_phone: patient.insurance_phone || ''
  });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid ' + T.b200, borderRadius: 8, fontSize: 13.5, boxSizing: 'border-box' };
  const lbl = { fontSize: 11, fontWeight: 700, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 };
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.patch(`${API}/api/emr/patients/${patient.id}/demographics`, f);
      onSaved(r.data.changed || {});
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || 'Save failed');
      setBusy(false);
    }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.45)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 22, width: 'min(560px, 96vw)',
        maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Edit Demographics</div>
        <div style={{ fontSize: 12.5, color: T.inkSoft, marginBottom: 14 }}>
          Changes are recorded in the audit trail with before and after values.</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><div style={lbl}>First name</div><input style={inp} value={f.first_name} onChange={e => setF({ ...f, first_name: e.target.value })} /></div>
          <div><div style={lbl}>Last name</div><input style={inp} value={f.last_name} onChange={e => setF({ ...f, last_name: e.target.value })} /></div>
          <div><div style={lbl}>Date of birth</div><input type="date" style={inp} value={f.dob} onChange={e => setF({ ...f, dob: e.target.value })} /></div>
          <div><div style={lbl}>Gender</div>
            <select style={inp} value={f.gender} onChange={e => setF({ ...f, gender: e.target.value })}>
              <option value="">{'\u2014'}</option><option value="male">male</option>
              <option value="female">female</option><option value="other">other</option>
            </select></div>
          <div><div style={lbl}>Phone</div><input style={inp} value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
          <div><div style={lbl}>Email</div><input style={inp} value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>Address</div><input style={inp} value={f.address} onChange={e => setF({ ...f, address: e.target.value })} /></div>
          <div><div style={lbl}>City</div><input style={inp} value={f.city} onChange={e => setF({ ...f, city: e.target.value })} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>State</div><input style={inp} value={f.state} onChange={e => setF({ ...f, state: e.target.value })} /></div>
            <div><div style={lbl}>Zip</div><input style={inp} value={f.zip} onChange={e => setF({ ...f, zip: e.target.value })} /></div>
          </div>
          <div><div style={lbl}>Pronouns</div><input style={inp} placeholder="e.g. he/him" value={f.pronouns} onChange={e => setF({ ...f, pronouns: e.target.value })} /></div>
          <div><div style={lbl}>Preferred language</div><input style={inp} value={f.preferred_language} onChange={e => setF({ ...f, preferred_language: e.target.value })} /></div>
          <div><div style={lbl}>Marital status</div>
            <select style={inp} value={f.marital_status} onChange={e => setF({ ...f, marital_status: e.target.value })}>
              <option value="">{'\u2014'}</option><option value="single">Single</option>
              <option value="married">Married</option><option value="divorced">Divorced</option>
              <option value="separated">Separated</option><option value="widowed">Widowed</option>
              <option value="partnered">Partnered</option>
            </select></div>
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid ' + T.b100, paddingTop: 10, marginTop: 4,
            fontSize: 11, fontWeight: 700, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Insurance details</div>
          <div><div style={lbl}>Plan type</div>
            <select style={inp} value={f.plan_type} onChange={e => setF({ ...f, plan_type: e.target.value })}>
              <option value="">{'\u2014'}</option><option value="PPO">PPO</option>
              <option value="HMO">HMO</option><option value="EPO">EPO</option>
              <option value="POS">POS</option><option value="Medicare">Medicare</option>
              <option value="Medicaid">Medicaid</option><option value="Other">Other</option>
            </select></div>
          <div><div style={lbl}>Insurance phone</div><input style={inp} value={f.insurance_phone} onChange={e => setF({ ...f, insurance_phone: e.target.value })} /></div>
          <div><div style={lbl}>Effective date</div><input type="date" style={inp} value={f.insurance_effective_date} onChange={e => setF({ ...f, insurance_effective_date: e.target.value })} /></div>
          <div><div style={lbl}>Termination date</div><input type="date" style={inp} value={f.insurance_termination_date} onChange={e => setF({ ...f, insurance_termination_date: e.target.value })} /></div>
        </div>
        {err && <div style={{ color: T.blocking, fontSize: 13, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button disabled={busy} onClick={onClose}
            style={{ background: '#fff', color: T.ink, border: '1px solid ' + T.b200, borderRadius: 8, padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button disabled={busy} onClick={save}
            style={{ background: '#4479B0', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            {busy ? 'Saving\u2026' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

export default function EMR({ user, nav }) {
  window.__tcio_user = user;
  const [page, setPage] = useState('patients');
  const [schedulePrefill, setSchedulePrefill] = useState(null);
  const [roundsPrefill, setRoundsPrefill] = useState(null);   // episode_id to auto-open on the Rounds board
  const [patients, setPatients] = useState([]);
  const [facility, setFacility] = useState('ALL');
  const [emrFacs, setEmrFacs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/facilities?emr_live=1&include_setup=1`).then(r => {
      if (r.data && r.data.ok) setEmrFacs((r.data.data || []).filter(f => f.emr_live || f.emr_setup_access)); // setup-mode facilities for people on the setup list
    }).catch(() => {});
  }, []);
  const didDefaultFacility = React.useRef(false);
  useEffect(() => {
    if (didDefaultFacility.current) return;
    const names = [...new Set(patients.map(p => p.facility_name).filter(Boolean))];
    if (names.length === 0) return;
    didDefaultFacility.current = true;
    const real = emrFacs.filter(f => !/sandbox/i.test(f.raw_name || ''));
    let sharedFac = null;
    try { sharedFac = JSON.parse(localStorage.getItem('tcio_facility') || 'null'); } catch (e) {}
    if (sharedFac && sharedFac.name && names.includes(sharedFac.name)) setFacility(sharedFac.name);
    else if (real.length === 1) setFacility(real[0].raw_name);
    else if (names.includes('TCIO Sandbox')) setFacility('TCIO Sandbox');
  }, [patients, emrFacs]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [demoEdit, setDemoEdit] = useState(false);
  const canEditDemo = ['program_manager', 'admin', 'ceo'].includes((user && user.role) || '');
  const [houses, setHouses] = useState([]);
  const [err, setErr] = useState('');
  const [dxForm, setDxForm] = useState(null);
  // EPISODE PICKER (2026-09-13, Chad-ratified episode wall): the chart renders the
  // SELECTED episode; current by default. Prior episodes are deliberate one-click history.
  const [selEpId, setSelEpId] = useState(null);
  const selEp = (sheet && (sheet.episodes.find(ep => ep.id === selEpId)
    || sheet.episodes.find(ep => !ep.discharge_date) || sheet.episodes[0])) || {};
  const visDx = sheet ? sheet.diagnoses.filter(d => !d.episode_id || d.episode_id === selEp.id) : [];
  const epD = v => v ? new Date(String(v).slice(0,10) + 'T00:00:00')
    .toLocaleDateString('en-US', { month:'numeric', day:'numeric', year:'2-digit' }) : '';
  const [chartTab, setChartTab] = useState('overview');
  const [clientPassOpen, setClientPassOpen] = useState(false);
  const [houseEdit, setHouseEdit] = useState(null);
  const [viewMode, setViewMode] = useState('photos'); // 'list' | 'photos' - photos is the default landing view
  const emrPerms = JSON.parse(localStorage.getItem('tcio_emr_perms') || '{}');
  const canCosign = emrPerms.superuser === true || !!emrPerms.notes_cosign;
  const canProvider = emrPerms.superuser === true || !!emrPerms.provider_sign || ['ceo','admin'].includes((user && user.role) || '');
  const canManageForms = emrPerms.superuser === true || !!emrPerms.manage_clinical_users;
  const [cosignCount, setCosignCount] = useState(0);
  useEffect(() => {
    if (canCosign) axios.get(`${API}/api/emr/cosign-queue`).then(r => setCosignCount(r.data.total || 0)).catch(() => {});
  }, [page]);
  const [providerCount, setProviderCount] = useState(0);
  const billingQCount = useChartQuestionCount();   // chart questions from billing that are waiting on this person
  const complianceCount = useComplianceCount();   // compliance items waiting on this person
  useEffect(() => {
    if (canProvider) axios.get(`${API}/api/emr/provider-queue`).then(r => setProviderCount(r.data.count || 0)).catch(() => {});
  }, [page]);
  const [attention, setAttention] = useState({});
  useEffect(() => {
    if (facility === 'ALL' || viewMode !== 'photos') return;
    const fp = patients.find(p => p.facility_name === facility && p.facility_id);
    if (!fp) { setAttention({}); return; }
    axios.get(`${API}/api/emr/census-attention`, { params: { facility_id: fp.facility_id } })
      .then(r => setAttention((r.data && r.data.attention) || {}))
      .catch(() => setAttention({}));
  }, [facility, viewMode, patients]);
  const [photoSort, setPhotoSort] = useState('default');

  const loadPatients = useCallback(async () => {
    try { setPatients((await axios.get(`${API}/api/emr/patients`)).data); setErr(''); }
    catch { setErr('Could not load patients'); }
  }, []);
  const loadHouses = useCallback(async () => {
    try { setHouses((await axios.get(`${API}/api/emr/houses`)).data); setErr(''); }
    catch { setErr('Could not load houses'); }
  }, []);
  const loadSheet = useCallback(async (id) => {
    try { setSheet((await axios.get(`${API}/api/emr/facesheet/${id}`)).data); setErr(''); }
    catch { setErr('Could not load face sheet'); }
  }, []);

  useEffect(() => { loadPatients(); loadHouses(); }, [loadPatients, loadHouses]);

  const openPatient = (id) => { setSelected(id); setSheet(null); setSelEpId(null); setPage('facesheet'); loadSheet(id); };

  useEffect(() => {
    try {
      const pid = sessionStorage.getItem('tcio_open_patient');
      if (!pid) return;
      sessionStorage.removeItem('tcio_open_patient');
      openPatient(pid);
      setChartTab('notes');
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [msgBusy, setMsgBusy] = useState(false);
  const msgCareTeam = async () => {
    if (!sheet) return;
    const activeEp = sheet.episodes.find(e => !e.discharge_date) || sheet.episodes[0] || null;
    setMsgBusy(true);
    try {
      const r = await axios.post(`${API}/api/msg/threads/care-team`, {
        patient_id: selected,
        facility_id: activeEp ? activeEp.facility_id : null,
      });
      sessionStorage.setItem('tcio_open_thread', r.data.id);
      if (nav) nav('messages');
    } catch { setErr('Could not open care-team thread'); }
    setMsgBusy(false);
  };

  const saveDx = async () => {
    if (!dxForm.icd10_code || !dxForm.description) return;
    try {
      await axios.post(`${API}/api/emr/patients/${selected}/diagnoses`, { ...dxForm, episode_id: dxForm.episode_id || (selEp ? selEp.id : null) });
      setDxForm(null); loadSheet(selected);
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.error) || 'Could not save diagnosis'); }
  };
  const saveHouse = async () => {
    try {
      await axios.patch(`${API}/api/emr/houses/${houseEdit.id}`, {
        license_number: houseEdit.license_number,
        licensed_capacity: houseEdit.licensed_capacity === '' ? null : Number(houseEdit.licensed_capacity),
        house_type: houseEdit.house_type,
      });
      setHouseEdit(null); loadHouses(); loadPatients();
    } catch { setErr('Could not save house'); }
  };

  const moveHouse = async (h, dir) => {
    const sibs = houses.filter(x => x.facility_name === h.facility_name)
      .sort((a,b) => Number(a.sort_order) - Number(b.sort_order));
    const i = sibs.findIndex(x => x.id === h.id);
    const j = i + dir;
    if (j < 0 || j >= sibs.length) return;
    try {
      await axios.patch(`${API}/api/emr/houses/${sibs[i].id}`, { sort_order: Number(sibs[j].sort_order) });
      await axios.patch(`${API}/api/emr/houses/${sibs[j].id}`, { sort_order: Number(sibs[i].sort_order) });
      loadHouses();
    } catch { setErr('Could not reorder'); }
  };

  const [certEdit, setCertEdit] = useState(null); // {facility_id, facility_name, value}
  const [medPassFac, setMedPassFac] = useState(null);
  const [priorFac, setPriorFac] = useState(null); // {facility_id, facility_name} — Import prior records (mig 344) // {facility_id, facility_name} — Medication settings (mig 208)
  const saveCert = async () => {
    try {
      await axios.patch(`${API}/api/emr/facilities/${certEdit.facility_id}/certification`,
        { op_certification: certEdit.value });
      setCertEdit(null); loadHouses();
    } catch { setErr('Could not save certification'); }
  };

  const facilityNames = useMemo(
    () => orderFacilities([...new Set([...patients.map(p => p.facility_name), ...emrFacs.map(f => f.raw_name)].filter(Boolean))]),
    [patients, emrFacs]);

  // MY CASELOAD (Chad 11/2): one click to see only clients where I'm the therapist or case manager. Remembered per browser.
  const [mine, setMine] = useState(() => { try { return localStorage.getItem('tcio_emr_mine') === '1'; } catch { return false; } });
  const toggleMine = () => setMine(m => { try { localStorage.setItem('tcio_emr_mine', m ? '0' : '1'); } catch {} return !m; });
  const mineCount = patients.filter(p => p.is_mine).length;
  const matches = useCallback((p) => {
    if (mine && !p.is_mine) return false;
    const q = search.toLowerCase();
    return !q || `${p.first_name} ${p.last_name}`.toLowerCase().includes(q)
      || (p.mrn || '').toLowerCase().includes(q);
  }, [search, mine]);

  const flatList = patients.filter(p => matches(p));

  // Photos view: one card per PATIENT (step-downs collapse to the active episode; Chad ruling 08-07)
  const dedupByPatient = (list) => {
    const by = {};
    for (const p of list) {
      const cur = by[p.id];
      if (!cur) { by[p.id] = p; continue; }
      const pActive = !p.discharge_date, cActive = !cur.discharge_date;
      if (pActive && !cActive) by[p.id] = p;
      else if (pActive === cActive && String(p.admit_date || '') > String(cur.admit_date || '')) by[p.id] = p;
    }
    return Object.values(by);
  };

  // Priority sort (prep; production default unchanged). Uses the attention severity already fetched.
  const SEV_RANK = { critical: 0, warning: 1, info: 2, ok: 3 };
  const sortPhotos = (list) => {
    if (photoSort !== 'priority') return list;
    return [...list].sort((a, b) => {
      const ra = attention[a.id] ? (SEV_RANK[attention[a.id].severity] ?? 3) : 4;
      const rb = attention[b.id] ? (SEV_RANK[attention[b.id].severity] ?? 3) : 4;
      return ra - rb;
    });
  };

  // Group selected facility's patients by house
  const facilityHouses = useMemo(() => {
    if (facility === 'ALL') return [];
    const pts = patients.filter(p => p.facility_name === facility && matches(p));
    const byHouse = {};
    for (const p of pts) {
      const key = p.house_name || 'Unassigned';
      (byHouse[key] = byHouse[key] || []).push(p);
    }
    // house metadata (occupancy incl. patients hidden by search, license) from houses list
    const meta = {};
    for (const h of houses.filter(h => h.facility_name === facility)) meta[h.name] = h;
    for (const h of houses.filter(h => h.facility_name === facility)) {
      if (!byHouse[h.name]) byHouse[h.name] = [];
    }
    const names = Object.keys(byHouse).sort((a,b) => {
      const sa = meta[a] ? Number(meta[a].sort_order) : 999;
      const sb = meta[b] ? Number(meta[b].sort_order) : 999;
      return sa - sb || a.localeCompare(b);
    });
    return names.map(n => ({ name:n, patients:byHouse[n], meta: meta[n] || null }));
  }, [facility, patients, houses, matches]);

  const ATT_COLOR = { critical: T.blocking, warning: T.warning, info: '#0878E8' };
  const attCell = (p) => {
    const a = attention[p.id];
    if (!a || !a.top || a.severity === 'ok' || a.severity === 'clear')
      return <span style={{ color: T.inkSoft }}>{'\u2014'}</span>;
    const c = ATT_COLOR[a.severity] || '#0878E8';
    return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
        <span style={{ width:7, height:7, borderRadius:99, background:c, flex:'none' }} />
        <span style={{ fontSize:12.5, color:T.ink }}>{a.top.label}{a.top.detail ? ' \u00B7 ' + a.top.detail : ''}</span>
        {a.more_count > 0 && <span style={{ fontSize:11.5, color:T.inkSoft }}>+{a.more_count}</span>}
      </span>
    );
  };

  const patientRow = (p) => (
    <tr key={p.id} onClick={() => openPatient(p.id)}
      style={{ cursor:'pointer' }}
      onMouseEnter={e => e.currentTarget.style.background = T.b100}
      onMouseLeave={e => e.currentTarget.style.background = ''}>
      <td style={{ ...td, fontWeight:600 }}>{p.last_name}, {p.first_name} {p.discharge_date && <span style={{ marginLeft:8, fontSize:10.5, fontWeight:600, color:'#7f1d1d', background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:10, padding:'2px 8px', whiteSpace:'nowrap' }}>DC {new Date(p.discharge_date).toLocaleDateString('en-US',{month:'numeric',day:'numeric',timeZone:'UTC'})} — doc due</span>}</td>
      <td style={{ ...td, fontFamily:'monospace', fontSize:12.5, color:T.inkSoft }}>{p.mrn}{p.intake_incomplete && <StatusDot label="INTAKE" style={{ marginLeft:7 }} />}</td>
      <td style={td}>{attCell(p)}</td>
      <td style={td}>{locPill(p.loc)}</td>
      <td style={td}>{p.day_of_stay ? `Day ${p.day_of_stay}` : '—'}</td>
      <td style={td}>{p.primary_payer || '—'}</td>
    </tr>
  );

  return (
    <div style={{ background:T.bg, minHeight:'100vh', padding:20, color:T.ink,
      fontFamily:"'Inter', system-ui, sans-serif", fontSize:14 }}>

      <div style={{ display:'flex', gap:10, marginBottom:18, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ fontSize:20, fontWeight:600, color:T.ink, marginRight:10 }}>EMR</div>
        <SegmentedTabs size="sm" active={page} onChange={setPage}
          tabs={[['patients','Census'],['groups','Groups'],['schedule','Group Schedule'],...(canCosign ? [['cosign', cosignCount > 0 ? `Cosign (${cosignCount})` : 'Cosign']] : []),...(canProvider ? [['provider', providerCount > 0 ? `Provider (${providerCount})` : 'Provider']] : []),['billingq', billingQCount > 0 ? <span>Billing questions<span style={{ display: 'inline-block', marginLeft: 6, minWidth: 18, padding: '1px 6px', borderRadius: 10, background: '#D92D20', color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center' }}>{billingQCount}</span></span> : 'Billing questions'],['audit','Chart Audit'],['compliance', complianceCount > 0 ? <span>Compliance<span style={{ display: 'inline-block', marginLeft: 6, minWidth: 18, padding: '1px 6px', borderRadius: 10, background: '#D92D20', color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center' }}>{complianceCount}</span></span> : 'Compliance'],...(canManageForms ? [['forms','Forms']] : []),['rounds','Rounds'],['labs','Labs'],['discharged','Discharged'],['houses','Houses & Licenses']]} />
        {err && <span style={{ color:T.blocking, fontSize:13 }}>⚠ {err}</span>}
      </div>

      {/* ── FORMS (cosign policy admin) ── */}
      {page === 'forms' && canManageForms && <FormBuilder domain="emr" user={user} />}

      {/* ── CENSUS ── */}
      {page === 'patients' && (
        <div>
          <TcioInsights api={API} token={user?.token || localStorage.getItem('tcio_token')} facilityId={facility === 'ALL' ? null : ((patients.find(p => p.facility_name === facility) || {}).facility_id || (emrFacs.find(f => f.raw_name === facility) || {}).id || null)} />
          <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
            <select value={facility} onChange={e => { const v = e.target.value; setFacility(v); try { const fid = ((patients.find(p => p.facility_name === v) || {}).facility_id) || ((emrFacs.find(f => f.raw_name === v) || {}).id) || null; if (v !== 'ALL') localStorage.setItem('tcio_facility', JSON.stringify({ id: fid, name: v })); } catch (err) {} }}
              style={{ ...input, fontWeight:600, minWidth:250 }}>
              <option value="ALL">All Patients ({patients.length})</option>
              {facilityNames.map(f => (
                <option key={f} value={f}>{(emrFacs.find(x => x.raw_name === f) || {}).org_name || f} ({patients.filter(p => p.facility_name === f).length})</option>
              ))}
            </select>
            <input style={{ ...input, minWidth:240 }} placeholder="Search name or MRN…"
              value={search} onChange={e => setSearch(e.target.value)} />

            {viewMode !== 'rounds' ? (
              <select value={photoSort} onChange={e => setPhotoSort(e.target.value)}
                style={{ ...input, width:'auto', fontSize:12.5, padding:'6px 10px' }}>
                <option value="default">Sort: Default</option>
                <option value="priority">Sort: Priority</option>
              </select>
            ) : null}
            <button onClick={toggleMine} title="Only clients where you are the therapist or case manager"
              style={{ ...btn(mine), padding:'6px 12px', fontSize:12.5, marginLeft:8 }}>★ My caseload{mineCount ? ` (${mineCount})` : ''}</button>
            <span style={{ display:'inline-flex', gap:4, marginLeft:'auto' }}>
              {[['list','☰ List'],['photos','▦ Photos'],['rounds','⏱ Rounds']].map(([k,l]) => (
                <button key={k} onClick={() => setViewMode(k)}
                  style={{ ...btn(viewMode===k), padding:'6px 12px', fontSize:12.5 }}>{l}</button>
              ))}
            </span>
          </div>

          {viewMode === 'rounds' ? (
            <Rounds user={user} initialFacilityName={facility === 'ALL' ? null : facility} />
          ) : viewMode === 'photos' ? (
            <PhotoGrid onOpen={openPatient}
              attention={facility === 'ALL' ? null : attention}
              patients={sortPhotos(dedupByPatient(facility === 'ALL' ? flatList
                : facilityHouses.flatMap(h => h.patients)))} />
          ) : facility === 'ALL' ? (
            <div style={{ ...card, overflow:'hidden' }}>
              <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}` }}>
                <strong>All Patients ({flatList.length})</strong>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ background:T.b100 }}>
                  {['Patient','MRN','LOC','Day','Payer','Facility','House'].map(h => <th key={h} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {flatList.map(p => (
                    <tr key={p.id} onClick={() => openPatient(p.id)}
                      style={{ cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = T.b100}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ ...td, fontWeight:600 }}>{p.last_name}, {p.first_name} {p.discharge_date && <span style={{ marginLeft:8, fontSize:10.5, fontWeight:600, color:'#7f1d1d', background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:10, padding:'2px 8px', whiteSpace:'nowrap' }}>DC {new Date(p.discharge_date).toLocaleDateString('en-US',{month:'numeric',day:'numeric',timeZone:'UTC'})} — doc due</span>}</td>
                      <td style={{ ...td, fontFamily:'monospace', fontSize:12.5, color:T.inkSoft }}>{p.mrn}{p.intake_incomplete && <StatusDot label="INTAKE" style={{ marginLeft:7 }} />}</td>
                      <td style={td}>{locPill(p.loc)}</td>
                      <td style={td}>{p.day_of_stay ? `Day ${p.day_of_stay}` : '—'}</td>
                      <td style={td}>{p.primary_payer || '—'}</td>
                      <td style={td}>{p.facility_name || '—'}</td>
                      <td style={td}>{p.house_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            facilityHouses.map(h => {
              const cap = h.meta ? Number(h.meta.bed_count) : null;
              const occ = h.meta ? Number(h.meta.occupied) : h.patients.length;
              const open = cap != null ? cap - occ : null;
              return (
                <div key={h.name} style={{ ...card, marginBottom:16, overflow:'hidden', borderRadius:8, boxShadow:'0 1px 2px rgba(20,45,70,0.04)' }}>
                  <div style={{ padding:'11px 18px', background:T.b100, borderBottom:`1px solid ${T.b200}`,
                    display:'flex', alignItems:'baseline', gap:14, flexWrap:'wrap' }}>
                    {h.meta && (
                      <span className="census-reorder" style={{ display:'inline-flex', gap:3 }}>
                        <button style={{ ...btn(false), padding:'1px 7px', fontSize:12 }}
                          title="Move house up" onClick={() => moveHouse(h.meta, -1)}>↑</button>
                        <button style={{ ...btn(false), padding:'1px 7px', fontSize:12 }}
                          title="Move house down" onClick={() => moveHouse(h.meta, 1)}>↓</button>
                      </span>
                    )}
                    <span style={{ fontWeight:600, fontSize:16, color:T.ink }}>{h.name}</span>
                    {cap != null && (
                      <span style={{ fontFamily:'monospace', fontSize:13 }}>
                        <span style={{ color: occ >= cap ? T.blocking : T.pass, fontWeight:600 }}>{occ}</span> / {cap}
                      </span>
                    )}
                    <span style={{ marginLeft:'auto', fontFamily:'monospace', fontSize:11, color:T.inkSoft }}>
                      {h.meta && h.meta.house_type === 'sober_living'
                        ? (h.meta.op_certification
                            ? `OP Cert ${h.meta.op_certification}`
                            : <span style={{ color:T.warning, fontWeight:600 }}>OP cert — not entered</span>)
                        : h.meta && h.meta.house_type === 'program'
                        ? 'Program'
                        : (h.meta && h.meta.license_number
                            ? `Lic ${h.meta.license_number}`
                            : <span style={{ color:T.warning, fontWeight:600 }}>license — missing</span>)}
                    </span>
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr>
                      {['CLIENT','MRN / STATUS','ATTENTION','LOC','DAY','PAYER'].map(hd => (
                        <th key={hd} style={{ padding:'7px 18px', textAlign:'left', fontSize:10.5, fontWeight:600,
                          letterSpacing:'0.06em', color:T.inkSoft, borderBottom:'1px solid ' + T.b100 }}>{hd}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {h.patients.length === 0
                        ? <tr><td style={{ ...td, fontStyle:'italic', color:T.inkSoft }}>No patients{search ? ' matching search' : ''}</td></tr>
                        : sortPhotos(h.patients).map(patientRow)}
                      {open != null && open > 0 && (
                        <tr><td colSpan={6} style={{ padding:'8px 18px', color:T.pass, fontSize:13 }}>＋ {open} open bed{open === 1 ? '' : 's'}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── FACE SHEET ── */}
      {page === 'facesheet' && (
        <div>
          <button style={{ ...btn(false), marginBottom:14 }} onClick={() => setPage('patients')}>← Census</button>
          {!sheet ? <div style={{ color:T.inkSoft }}>Loading…</div> : (
            <div>
              <div style={{ ...card, borderLeft:`4px solid ${T.rose}`, padding:'16px 22px',
                display:'flex', flexWrap:'wrap', gap:'8px 34px', alignItems:'baseline', marginBottom:16 }}>
                <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                  <div style={{ position:'relative' }}>
                    <PatientAvatar patient={sheet.patient} size={64} radius={10} />
                  </div>
                <div>
                  <div style={{ fontSize:22, fontWeight:600,
                    display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    <span>{sheet.patient.last_name}, {sheet.patient.first_name}</span>
                    <span style={{ fontFamily:'inherit' }}>
                      <AllergyBanner compact active={true} patientId={selected} /></span>
                  </div>
                  <div style={{ fontFamily:'monospace', fontSize:13, color:T.inkSoft }}>
                    <MrnText patientId={selected} facilityId={selEp ? selEp.facility_id : null} /> · DOB {sheet.patient.dob ? new Date(sheet.patient.dob).toLocaleDateString('en-US', { timeZone:'UTC' }) : '\u2014'}{sheet.patient.dob ? ` (${Math.floor((Date.now() - new Date(sheet.patient.dob).getTime()) / 31557600000)})` : ''} · {sheet.patient.gender || '\u2014'}
                  </div>
                  {selEp.facility_name && (
                    <div style={{ fontSize:13, color:T.inkSoft, marginTop:2 }}>
                      {selEp.facility_name}{selEp.facility_phone ? ` · ${selEp.facility_phone}` : ''}
                    </div>)}
                  <div style={{ marginTop:6, display:'flex', gap:8, alignItems:'center' }}>
                    <PhotoUpload patientId={sheet.patient.id} hasPhoto={!!sheet.patient.photo_key} onDone={() => { loadSheet(selected); loadPatients(); }} />
                    {canEditDemo &&
                      <button onClick={() => setDemoEdit(true)}
                        style={{ background:'#fff', color:'#4479B0', border:'1px solid #D8E3EE',
                          borderRadius:8, padding:'5px 11px', fontWeight:600, fontSize:12, cursor:'pointer' }}>
                        Edit Demographics</button>}
                  </div>
                  {demoEdit && sheet && sheet.patient &&
                    <DemographicsEditModal patient={sheet.patient}
                      onClose={() => setDemoEdit(false)}
                      onSaved={() => { setDemoEdit(false); loadSheet(selected); loadPatients(); }} />}
                </div>
                </div>
                {[selEp].filter(e => e && e.id).map(e => (
                  <React.Fragment key={e.id}>
                    <div><div style={label}>Admitted</div><div style={{ fontWeight:500 }}>
                      {e.admit_date ? new Date(String(e.admit_date).slice(0,10) + 'T00:00:00').toLocaleDateString('en-US') : '—'}
                      {e.admitted_at && <span style={{ fontWeight:400, color:T.inkSoft }}> {new Date(e.admitted_at).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}</span>} {/* 10/24 Cyndie 92994300 */}
                    </div></div>
                    <div><div style={label}>Days in tx</div><div style={{ fontWeight:600, fontSize:16 }}>{e.day_of_stay ?? '—'}</div></div>
                    <div><div style={label}>Level of care</div>{locPill(e.loc)} <span style={{ fontSize:13 }}>Day {e.day_of_stay}</span></div>
                    <div><div style={label}>Facility</div><div style={{ fontWeight:500 }}>{e.facility_name}</div></div>
                    <div><div style={label}>House / Bed</div><div style={{ fontWeight:500 }}>
                      {(!e.discharge_date && sheet.current_bed) ? `${sheet.current_bed.house_name} · ${sheet.current_bed.bed_label}` : (e.bed_id || '—')}
                    </div></div>
                  </React.Fragment>
                ))}
                <div><div style={label}>Primary therapist</div><div style={{ fontWeight:500 }}>{(sheet.staff && sheet.staff.therapist) || '—'}</div></div>
                <div><div style={label}>Case manager</div><div style={{ fontWeight:500 }}>{(sheet.staff && sheet.staff.case_manager) || '—'}</div></div>
                <div><div style={label}>Payer</div><div style={{ fontWeight:500 }}>{sheet.patient.primary_payer || '—'}</div></div>
                <div><div style={label}>Member ID</div><div style={{ fontFamily:'monospace', fontSize:13 }}>{sheet.patient.member_id || '—'}</div></div>
                <div><div style={label}>Expected DC</div><div style={{ fontWeight:500 }}>{(sheet.episode_details && sheet.episode_details.expected_dc) ? new Date(sheet.episode_details.expected_dc).toLocaleDateString('en-US', { timeZone:'UTC' }) : 'Not Set'}</div></div>
                <div><div style={label}>ROI</div><div onClick={() => { const el = document.getElementById('contacts-roi-card'); if (el) el.scrollIntoView({ behavior:'smooth', block:'center' }); }}
                  style={{ fontWeight:600, cursor:'pointer', color: (sheet.active_roi_count || 0) > 0 ? '#16A36A' : '#E99A18' }}>
                  {(sheet.active_roi_count || 0) > 0 ? `${sheet.active_roi_count} Active` : 'None'}</div></div>
                <div style={{ marginLeft:'auto', alignSelf:'center' }}>
                  <button style={btn(true)} onClick={msgCareTeam} disabled={msgBusy}>
                    {msgBusy ? 'Opening...' : 'Message Care Team'}
                  </button>
                </div>
              </div>

              {sheet.episodes.length > 1 && (
                <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
                  <span style={label}>Episode</span>
                  {sheet.episodes.map(e => (
                    <button key={e.id} onClick={() => setSelEpId(e.id)}
                      style={{ ...btn(e.id === selEp.id), padding:'5px 12px', fontSize:12 }}>
                      {e.discharge_date
                        ? epD(e.admit_date) + ' \u2013 ' + epD(e.discharge_date) + ' \u00b7 Discharged'
                        : 'Current \u00b7 adm ' + epD(e.admit_date)}
                    </button>
                  ))}
                </div>
              )}
              <SegmentedTabs active={chartTab} onChange={setChartTab} style={{ marginBottom:16 }}
                tabs={[['overview','Overview'],['intake','Intake'],['prescreen','Prescreen'],['tp','Treatment Plan'],['notes','Clinical'],['drnursing','Dr/Nursing'],['drorders',"Dr's Orders"],['labs','Labs'],...(((emrFacs.find(f => f.id === (selEp && selEp.facility_id)) || {}).med_admin_model === 'prescribe_only') ? [] : [['mar','MAR']]),['bht','BHT'],['discharge','Discharge'],['records','Records']]} />

              <div style={{ display: chartTab === 'records' ? 'block' : 'none' }}>
                <PriorRecordPanel patientId={selected} />
                <AttachmentsPanel patientId={selected} />
                <EMRRecords patientId={selected} />
              </div>

              <div style={{ display: chartTab === 'overview' ? 'grid' : 'none', gridTemplateColumns: window.innerWidth < 760 ? '1fr' : '2fr 1fr', gap:16, alignItems:'start' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  {selEp.discharge_date ? (
                    <div>
                      <div style={{ ...card, padding:'12px 16px', marginBottom:16, fontSize:13,
                        color:T.inkSoft, display:'flex', gap:8, alignItems:'center' }}>
                        <span style={{ fontWeight:700, color:T.ink }}>Closed episode</span>
                        <span>{epD(selEp.admit_date)} {'\u2013'} {epD(selEp.discharge_date)} {'\u00b7'} Discharged {'\u00b7'} no scheduled care due</span>
                      </div>
                      <SafetySnapshot patientId={sheet.patient.id} episodeId={selEp.id}
                        safety={sheet.safety} onGoTab={setChartTab} />
                    </div>
                  ) : (
                  <CareToday patientId={sheet.patient.id}
                    episodeId={selEp.id}
                    onGoTab={setChartTab}
                    snapshot={<SafetySnapshot patientId={sheet.patient.id}
                      episodeId={selEp.id}
                      safety={sheet.safety} onGoTab={setChartTab} />} />
                  )}
                </div>
                <div style={{ display:'grid', gap:16, alignContent:'start', order: window.innerWidth < 760 ? 2 : 0 }}>
                <div style={card}>
                  <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}`,
                    display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <strong>Diagnoses</strong>
                    <button style={btn(true)} onClick={() =>
                      setDxForm({ icd10_code:'', description:'', is_primary: visDx.length === 0 })}>+ Add</button>
                  </div>
                  <div style={{ padding:18 }}>
                    <DxFindings patientId={sheet.patient.id} onChanged={() => loadSheet(selected)} />
                    {visDx.length === 0 && !dxForm &&
                      <div style={{ color:T.inkSoft }}>No diagnoses recorded yet.</div>}
                    {visDx.map(d => (
                      <div key={d.id} style={{ padding:'8px 0', borderBottom:`1px solid ${T.b100}` }}>
                        <span style={{ fontFamily:'monospace', fontWeight:600 }}>{d.icd10_code}</span>
                        {' '}{d.description}
                        {d.is_primary && <span style={{ marginLeft:8, background:T.passBg, color:T.pass,
                          borderRadius:99, padding:'1px 9px', fontSize:11, fontWeight:600 }}>PRIMARY</span>}
                      </div>
                    ))}
                    {dxForm && (
                      <div style={{ marginTop:12, display:'grid', gap:9 }}>
                        <DxTypeahead dxForm={dxForm} setDxForm={setDxForm} />
                        <input style={input} placeholder="Description (fills automatically when you pick a code)"
                          value={dxForm.description} onChange={e => setDxForm({ ...dxForm, description:e.target.value })} />
                        <label style={{ fontSize:13 }}>
                          <input type="checkbox" checked={dxForm.is_primary}
                            onChange={e => setDxForm({ ...dxForm, is_primary:e.target.checked })} /> Primary diagnosis
                        </label>
                        <div style={{ display:'flex', gap:8 }}>
                          <button style={btn(true)} onClick={saveDx}>Save diagnosis</button>
                          <button style={btn(false)} onClick={() => setDxForm(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={card}>
                  <div style={{ padding:'13px 18px', borderBottom:`1px solid ${T.b100}` }}><strong>Episodes</strong></div>
                  <div style={{ padding:18 }}>
                    {(() => {
                      const curFac = (selEp.facility_id);
                      return sheet.episodes.filter(e => !curFac || e.facility_id === curFac);
                    })().map(e => (
                      <div key={e.id} style={{ padding:'8px 0', borderBottom:`1px solid ${T.b100}`,
                        display:'flex', gap:12, alignItems:'center' }}>
                        {locPill(e.loc)}
                        <span style={{ fontSize:13 }}>{e.admit_date && new Date(e.admit_date).toLocaleDateString('en-US', { timeZone:'UTC' })}
                          {' → '}{e.discharge_date ? new Date(e.discharge_date).toLocaleDateString('en-US', { timeZone:'UTC' }) :
                          <span style={{ color:T.pass, fontWeight:600 }}>current</span>}</span>
                        <span style={{ color:T.inkSoft, fontSize:13 }}>{e.facility_name}</span>
                      </div>
                    ))}
                    {(sheet.prior_episodes || 0) > 0 && (
                      <div style={{ marginTop: 10, fontSize: 12.5, color: T.inkSoft }}>
                        Prior episodes: <strong style={{ color: T.ink }}>{sheet.prior_episodes}</strong></div>)}
                  </div>
                </div>
                <InsuranceCard patient={sheet.patient} epDetails={sheet.episode_details} />
                <PatientFinancial patientId={sheet.patient.id} role={user && user.role} />
                <div style={{ order: (user && user.role) === 'ur_coordinator' ? -1 : 0 }}>
                <UrAuthCard patientId={sheet.patient.id} role={user && user.role}
                  episodeId={selEp.id} />
                </div>
                <RecentlyResulted patientId={sheet.patient.id} onGoTab={setChartTab} />
                </div>
                <div style={{ display:'grid', gap:16, alignContent:'start', order: window.innerWidth < 760 ? 1 : 0 }}>
                  <div id="contacts-roi-card" style={{ order: ['medical','medical_admin'].includes(user && user.role) ? 2 : 0 }}>
                  <ContactsReleases patientId={sheet.patient.id}
                    episodeId={selEp.id} />
                  </div>
                  <div style={{ order: ['medical','medical_admin'].includes(user && user.role) ? 1 : 0 }}>
                  <TreatmentTeam staff={sheet.staff} onMessageTeam={msgCareTeam} />
                  </div>
                  <AdministrativeCard patient={sheet.patient} canValidate={canEditDemo}
                    onValidated={() => loadSheet(selected)} />
                </div>
                <div style={{ gridColumn: '1 / -1', order: 3 }}>
                  <QuickActions onGoTab={setChartTab} patientId={sheet.patient.id} role={user && user.role}
                    patientName={`${sheet.patient.last_name || ''}_${sheet.patient.first_name || ''}`} />
                </div>
              </div>
              <div style={{ display: chartTab === 'prescreen' ? 'block' : 'none' }}>
                <PrescreenTab patientId={selected} active={chartTab === 'prescreen'} />
              </div>

              <div style={{ display: chartTab === 'tp' ? 'block' : 'none' }}>
                <TPSpine episodeId={selEp.id} />
                <EMRNotes canCosign={['clinical_director','admin','ceo'].includes((user && user.role) || '')} patientId={selected} facilityName={(selEp.facility_name) || ''} categories={['treatment_plan']} formKeys={['master_treatment_plan']} heading="Master Treatment Plan (signed snapshot)"
                  episodeId={selEp.id} />
              </div>
              <div style={{ display: chartTab === 'intake' ? 'block' : 'none' }}>
                <AttachmentsPanel patientId={selected} sections={['client_id','insurance','belongings']} compact />
                <FacilityIntakePackets patientId={selected}
                  facilityName={(selEp.facility_name) || ''}
                  episode={selEp}
                  clientName={(sheet.patient ? `${sheet.patient.first_name || ''} ${sheet.patient.last_name || ''}`.trim() : '')}
                  clientPassOpen={clientPassOpen} setClientPassOpen={setClientPassOpen} />
              </div>
              {chartTab === 'discharge' && (
                <FacilityIntakePackets kind="discharge" patientId={selected}
                  facilityName={(selEp.facility_name) || ''}
                  episode={selEp}
                  clientName={(sheet.patient ? `${sheet.patient.first_name || ''} ${sheet.patient.last_name || ''}`.trim() : '')} />
              )}
              <div style={{ display: chartTab === 'notes' ? 'block' : 'none' }}>
                <EMRNotes canCosign={['clinical_director','admin','ceo'].includes((user && user.role) || '')} patientId={selected} facilityName={(selEp.facility_name) || ''} categories={['intake','clinical']}
                  episodeId={selEp.id} />
              </div>
              <div style={{ display: chartTab === 'drorders' ? 'block' : 'none' }}>
                <MedOrders active={chartTab === 'drorders'} patientId={selected}
                  episodeId={selEp.id} facilityId={selEp.facility_id} />
              </div>

              <div style={{ display: chartTab === 'labs' ? 'block' : 'none' }}>
                <AttachmentsPanel patientId={selected} sections={['ua']} compact />
                <EMRLabs active={chartTab === 'labs'} patientId={selected}
                  episodeId={selEp.id}
                  facilityId={(selEp.facility_id)} />
              </div>

              <div style={{ display: chartTab === 'mar' ? 'block' : 'none' }}>
                <MarDay active={chartTab === 'mar'} patientId={selected}
                  episodeId={selEp.id} />
                <ShiftCountPanel active={chartTab === 'mar'} facilityId={(selEp.facility_id)} />
                <MedInventory active={chartTab === 'mar'} patientId={selected}
                  episodeId={selEp.id} />
              </div>

              <div style={{ display: chartTab === 'drnursing' ? 'block' : 'none' }}>
                <AttachmentsPanel patientId={selected} sections={['ext_clinical','ua','outside','destruction']} compact />
                <EMRNotes canCosign={['clinical_director','admin','ceo'].includes((user && user.role) || '')} patientId={selected} facilityName={(selEp.facility_name) || ''} categories={['medical','nursing']}
                  episodeId={selEp.id} />
                <div style={{ height:16 }} />
                <RoundsHistory episodeId={selEp.id}
                  onStartRound={(eid) => { setRoundsPrefill(eid); setPage('rounds'); }} />
                <VitalsPanel episodeId={selEp.id}
                  patientName={(sheet.patient ? `${sheet.patient.first_name || ''} ${sheet.patient.last_name || ''}`.trim() : '')} />
              </div>
              <div style={{ display: chartTab === 'bht' ? 'block' : 'none' }}>
                <EMRNotes canCosign={['clinical_director','admin','ceo'].includes((user && user.role) || '')} patientId={selected} facilityName={(selEp.facility_name) || ''} categories={['nursing']} formKeys={['ciwa_ar','cows','uds_drug_screen']}
                  episodeId={selEp.id} />
                <div style={{ height:16 }} />
                <RoundsHistory episodeId={selEp.id}
                  onStartRound={(eid) => { setRoundsPrefill(eid); setPage('rounds'); }} />
                <VitalsPanel episodeId={selEp.id}
                  patientName={(sheet.patient ? `${sheet.patient.first_name || ''} ${sheet.patient.last_name || ''}`.trim() : '')} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── GROUPS ── */}
      {page === 'groups' && <EMRGroups patients={patients} prefill={schedulePrefill} user={user}
        onGoSchedule={() => setPage('schedule')} onPrefillConsumed={() => setSchedulePrefill(null)} />}

      {/* ── SCHEDULE ── */}
      {page === 'schedule' && <EMRSchedule patients={patients}
        onStartSession={(pf) => { setSchedulePrefill(pf); setPage('groups'); }}
        onOpenGroup={(id) => { setSchedulePrefill({ openInstanceId: id }); setPage('groups'); }} />}

      {/* ── CHART AUDIT ── */}
      {page === 'discharged' && <DischargedFinder facilityFilter={facility} canSeeSandbox={['ceo','admin'].includes((user && user.role) || '') || (user && user.can_see_sandbox === true)} onOpen={(pid) => openPatient(pid)} />}
      {page === 'cosign' && <CosignQueue user={user} onOpenChart={(pid) => { setPage('patients'); setSelected(pid); }} />}
      {page === 'provider' && <ProviderQueue user={user} onOpenChart={(pid) => { setPage('patients'); setSelected(pid); }} />}
      {page === 'billingq' && <MyChartQuestions onOpenChart={(pid) => { setPage('patients'); setSelected(pid); }} />}
      {page === 'audit' && <ChartAudit user={user} onOpenPatient={(pid) => openPatient(pid)} />}

      {/* ── COMPLIANCE ── */}
      {page === 'compliance' && <MyCompliance onOpenPatient={(pid) => openPatient(pid)} />}
      {page === 'compliance' && <EMRCompliance user={user} onOpenPatient={(pid, tabKey) => { openPatient(pid); if (tabKey) setChartTab(tabKey); }} />}

      {/* ── ROUNDS BOARD (top-level) ── */}
      {page === 'rounds' && <Rounds user={user} initialFacilityName={facility === 'ALL' ? null : facility}
        openEpisodeId={roundsPrefill} onConsumedOpen={() => setRoundsPrefill(null)} />}

      {/* ── LABS BOARD ── */}
      {page === 'labs' && <LabsBoard user={user} initialFacilityName={facility === 'ALL' ? null : facility}
        patients={patients} onOpenChart={(pid) => { setPage('patients'); setSelected(pid); }} />}

      {/* ── HOUSES & LICENSES ── */}
      {page === 'houses' && (
        <div style={{ ...card, overflow:'hidden' }}>
          <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.b100}` }}>
            <strong>Houses &amp; Licensed Units ({houses.length})</strong>
            <span style={{ color:T.inkSoft, marginLeft:10, fontSize:13 }}>Click a row to enter license number and capacity</span>
          </div>
          <div style={{ padding:'10px 18px', borderBottom:`1px solid ${T.b100}`, display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <span style={label}>Outpatient certifications:</span>
            {[...new Map(houses.map(h => [h.facility_id, h])).values()].map(h => (
              <button key={h.facility_id} style={{ ...btn(false), padding:'4px 10px', fontSize:12 }}
                onClick={() => setCertEdit({ facility_id:h.facility_id, facility_name:h.facility_name, value:h.op_certification || '' })}>
                {h.facility_name}: {h.op_certification || '—'}
              </button>
            ))}
            {certEdit && (
              <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                <strong style={{ fontSize:13 }}>{certEdit.facility_name}</strong>
                <input style={{ ...input, width:200 }} placeholder="OP certification #"
                  value={certEdit.value} onChange={e => setCertEdit({ ...certEdit, value:e.target.value })} />
                <button style={btn(true)} onClick={saveCert}>Save</button>
                <button style={btn(false)} onClick={() => setCertEdit(null)}>Cancel</button>
              </span>
            )}
          </div>
          <div style={{ padding:'10px 18px', borderBottom:`1px solid ${T.b100}`, display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <span style={label}>Medication settings:</span>
            {[...new Map(houses.map(h => [h.facility_id, h])).values()].map(h => (
              <button key={h.facility_id} style={{ ...btn(medPassFac?.facility_id === h.facility_id), padding:'4px 10px', fontSize:12 }}
                onClick={() => setMedPassFac(medPassFac?.facility_id === h.facility_id ? null : { facility_id:h.facility_id, facility_name:h.facility_name })}>
                {h.facility_name}
              </button>
            ))}
            <span style={{ color:T.inkSoft, fontSize:12 }}>Med-pass times, window, and aligning active orders</span>
          </div>
          {medPassFac && (
            <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.b100}`, background:'#fafbfc' }}>
              <MedModelPicker api={API} facilityId={medPassFac.facility_id} facilityName={medPassFac.facility_name} />
              <MedPassSettings api={API} facilityId={medPassFac.facility_id} facilityName={medPassFac.facility_name} />
            </div>
          )}
          <div style={{ padding:'10px 18px', borderBottom:`1px solid ${T.b100}`, display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <span style={label}>Import prior records:</span>
            {[...new Map(houses.map(h => [h.facility_id, h])).values()].map(h => (
              <button key={h.facility_id} style={{ ...btn(priorFac?.facility_id === h.facility_id), padding:'4px 10px', fontSize:12 }}
                onClick={() => setPriorFac(priorFac?.facility_id === h.facility_id ? null : { facility_id:h.facility_id, facility_name:h.facility_name })}>
                {h.facility_name}
              </button>
            ))}
            <span style={{ color:T.inkSoft, fontSize:12 }}>Charts downloaded from the previous EMR, matched to clients on the bed board</span>
          </div>
          {priorFac && (
            <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.b100}`, background:'#fafbfc' }}>
              <PriorRecordsImport api={API} facilityId={priorFac.facility_id} facilityName={priorFac.facility_name} />
            </div>
          )}
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:T.b100 }}>
              {['Order','House','Facility','Type','Beds','Occupied','License #','Capacity'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {houses.map(h => (
                <tr key={h.id} onClick={() => setHouseEdit({ id:h.id, name:h.name,
                    license_number:h.license_number || '', licensed_capacity:h.licensed_capacity ?? '',
                    house_type:h.house_type || 'licensed' })}
                  style={{ cursor:'pointer' }}>
                  <td style={{ ...td, whiteSpace:'nowrap' }} onClick={e => e.stopPropagation()}>
                    <button style={{ ...btn(false), padding:'2px 8px', marginRight:4 }} onClick={() => moveHouse(h, -1)}>↑</button>
                    <button style={{ ...btn(false), padding:'2px 8px' }} onClick={() => moveHouse(h, 1)}>↓</button>
                  </td>
                  <td style={{ ...td, fontWeight:600 }}>{h.name}</td>
                  <td style={td}>{h.facility_name}</td>
                  <td style={td}>{h.house_type === 'sober_living' ? 'Sober Living' : h.house_type === 'program' ? 'Program' : 'Licensed'}</td>
                  <td style={td}>{h.bed_count}</td>
                  <td style={td}>
                    <span style={{ color: Number(h.occupied) >= Number(h.bed_count) ? T.blocking : T.pass,
                      fontWeight:600 }}>{h.occupied}</span> / {h.bed_count}</td>
                  <td style={{ ...td, fontFamily:'monospace', fontSize:12.5 }}>
                    {h.license_number || <span style={{ color:T.warning }}>— missing</span>}</td>
                  <td style={td}>{h.licensed_capacity ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {houseEdit && (
            <div style={{ padding:18, borderTop:`2px solid ${T.b200}`, background:T.b100,
              display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' }}>
              <strong style={{ marginRight:6 }}>{houseEdit.name}</strong>
              <div><div style={label}>License number</div>
                <input style={{ ...input, width:220 }} value={houseEdit.license_number}
                  onChange={e => setHouseEdit({ ...houseEdit, license_number:e.target.value })} /></div>
              <div><div style={label}>Type</div>
                <select style={{ ...input, width:160 }} value={houseEdit.house_type}
                  onChange={e => setHouseEdit({ ...houseEdit, house_type:e.target.value })}>
                  <option value="licensed">Licensed</option>
                  <option value="sober_living">Sober Living</option>
                  <option value="program">Program</option>
                </select></div>
              <div><div style={label}>Licensed capacity</div>
                <input style={{ ...input, width:110 }} type="number" value={houseEdit.licensed_capacity}
                  onChange={e => setHouseEdit({ ...houseEdit, licensed_capacity:e.target.value })} /></div>
              <button style={btn(true)} onClick={saveHouse}>Save</button>
              <button style={btn(false)} onClick={() => setHouseEdit(null)}>Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
